<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonTree                                            |
| Section: components                                              |
| Purpose: Person-level tree shell with achternamen-style sidebar. |
+------------------------------------------------------------------+
-->
<template>
    <div class="person-tree-container" :data-layout-settled="String(layoutSettled)">
        <div class="tree-legend">
            <button v-for="item in legendItems" :key="item.category" type="button" class="tree-legend__item"
                :class="{ 'tree-legend__item--disabled': isLegendCategoryHidden(item.category) }"
                :aria-pressed="String(!isLegendCategoryHidden(item.category))"
                :title="$t(isLegendCategoryHidden(item.category) ? 'personTree.legend.showCategory' : 'personTree.legend.hideCategory')"
                @click="toggleLegendCategory(item.category)">
                <span class="tree-legend__swatch" :class="`tree-legend__swatch--${item.category}`"></span>
                <span class="tree-legend__labels">
                    <span class="tree-legend__label">{{ $t(item.primaryKey) }}</span>
                    <span class="tree-legend__sublabel">{{ $t(item.secondaryKey) }}</span>
                </span>
            </button>
        </div>
        <div v-if="activePathRoutes.length" class="tree-legend-path">
            <span class="tree-legend-path__title">{{ $t('personTree.legend.pathTitle') }}</span>
            <span v-for="(route, routeIndex) in activePathRoutes" :key="`route-${routeIndex}`"
                class="tree-legend-path__value">
                <span class="tree-route-swatch" :style="{ backgroundColor: getRouteColor(routeIndex) }"></span>
                <template v-for="(pathNode, index) in route" :key="`${routeIndex}-${pathNode.id}-${index}`">
                    <span v-if="index > 0" class="tree-legend-path__separator" aria-hidden="true"> &gt; </span>
                    <img v-if="isPrivateLabel(pathNode)" class="person-list-lock" :src="lockIconSrc"
                        :alt="privateLabelTitle(pathNode)" :title="privateLabelTitle(pathNode)" />
                    <span v-else>{{ getDisplayName(pathNode) }}</span>
                </template>
            </span>
            <router-link v-if="relationshipDetailsRoute" :to="relationshipDetailsRoute"
                class="tree-legend-path__details">{{ $t('personTree.legend.relationshipDetails') }}</router-link>
        </div>
        <div id="zoom-buttons">
            <button @click="zoomIn"><img src="@/assets/icons/zoom_in_red.svg" alt="Zoom In"></button>
            <button @click="zoomOut"><img src="@/assets/icons/zoom_out_red.svg" alt="Zoom Out"></button>
        </div>
        <button id="fullscreen-button" @click="toggleFullscreen"
            :title="isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'">
            <img v-if="!isFullscreen" src="@/assets/icons/fullscreen.svg" alt="Volledig scherm">
            <img v-else src="@/assets/icons/fullscreen_exit.svg" alt="Volledig scherm verlaten">
        </button>
        <button id="toggle-sidebar-button" @click="toggleSidebar" :class="{ 'sidebar-collapsed': sidebarCollapsed }"
            :title="sidebarCollapsed ? 'Personen tonen' : 'Personen verbergen'">
            <img v-if="sidebarCollapsed" src="@/assets/icons/chevron_right.svg" alt="Toon">
            <img v-else src="@/assets/icons/chevron_left.svg" alt="Verberg">
        </button>
        <div class="person-list-container" :class="{ collapsed: sidebarCollapsed }">
            <h1>{{ totalPeople }} personen</h1>
            <form class="person-name-filter" @submit.prevent="addNameFilters">
                <label for="person-name-filter-input">Filter stamboom op naam</label>
                <div class="person-name-filter__controls">
                    <input id="person-name-filter-input" v-model="nameFilterInput" type="text"
                        placeholder="Bijv. Wemaij, Akollo" autocomplete="off">
                    <button type="submit" :disabled="!nameFilterInput.trim()">Toevoegen</button>
                </div>
                <p class="person-name-filter__hint">Meerdere namen mogen met een komma. Directe relaties blijven zichtbaar.</p>
                <div v-if="nameFilters.length" class="person-name-filter__chips" aria-label="Actieve naamfilters">
                    <button v-for="filter in nameFilters" :key="filter" type="button" class="person-name-filter__chip"
                        :class="{ 'person-name-filter__chip--empty': nameFilterMatchCount(filter) === 0 }"
                        :title="nameFilterMatchCount(filter) === 0 ? 'Geen personen gevonden' : 'Filter verwijderen'"
                        @click="removeNameFilter(filter)">
                        {{ filter }} <span aria-hidden="true">×</span>
                    </button>
                    <button type="button" class="person-name-filter__clear" @click="clearNameFilters">Wis alles</button>
                </div>
                <p v-if="nameFilters.length" class="person-name-filter__result" aria-live="polite">
                    {{ nameFilterMatchedCount }} treffers · {{ nameFilterVisibleCount }} personen inclusief directe relaties
                </p>
            </form>
            <div class="person-list-search">
                <input v-model.trim="searchQuery" type="text" placeholder="Zoek in personenlijst..."
                    aria-label="Zoek in personenlijst">
            </div>
            <VirtualizedTreeList v-if="!sidebarCollapsed" ref="personList" :items="filteredNodeList" item-key="id"
                :estimated-row-height="40" :overscan="8" :initial-scroll-offset="listScrollOffset"
                aria-label="Personen" @scroll-offset="listScrollOffset = $event">
                <template #default="{ item: node }">
                    <div class="person-item"
                        :class="{ highlighted: node.id === selectedPerson || node.id === previewPerson }"
                        @click="selectPerson(node.id, false)">
                        <span class="person-name" :title="getDisplayName(node)">
                            <img v-if="isPrivateLabel(node)" class="person-list-lock" :src="lockIconSrc"
                                :alt="privateLabelTitle(node)" />
                            <template v-else>{{ getDisplayName(node) }}</template>
                        </span>
                        <div class="dropdown">
                            <button class="dropdown-toggle">
                                <img src="@/assets/icons/dropdown_white.svg" aria-label="Acties">
                            </button>
                            <div class="dropdown-menu">
                                <button v-if="!isPrivateNode(node)" @click.stop="goToPerson(node)">Open persoon</button>
                                <button @click.stop="goToSearch(node)">Zoeken</button>
                            </div>
                        </div>

                        <p class="secondary-label" v-if="node.id === selectedPerson && secondaryNodes.length">Directe
                            verwantschappen:</p>
                        <ul v-if="node.id === selectedPerson && secondaryNodes.length" class="secondary-list">
                            <li v-for="secondary in secondaryNodes" :key="secondary.id" class="secondary-item">
                                <img v-if="isPrivateLabel(secondary)" class="person-list-lock" :src="lockIconSrc"
                                    :alt="privateLabelTitle(secondary)" :title="privateLabelTitle(secondary)" />
                                <template v-else>{{ getDisplayName(secondary) }}</template>
                            </li>
                        </ul>

                        <p class="path-label" v-if="node.id === selectedPerson && pathRoutes.length">{{
                            $t('personTree.legend.pathTitle') }}</p>
                        <ul v-if="node.id === selectedPerson && pathRoutes.length" class="path-list">
                            <li v-for="(route, routeIndex) in pathRoutes" :key="`sidebar-route-${routeIndex}`"
                                class="path-route" :style="{ '--route-color': getRouteColor(routeIndex) }">
                                <span v-for="(pathNode, pathIndex) in route"
                                    :key="`${routeIndex}-${pathNode.id}-${pathIndex}`" class="path-item">
                                    <img v-if="isPrivateLabel(pathNode)" class="person-list-lock" :src="lockIconSrc"
                                        :alt="privateLabelTitle(pathNode)" :title="privateLabelTitle(pathNode)" />
                                    <template v-else>{{ getDisplayName(pathNode) }}</template>
                                </span>
                            </li>
                        </ul>
                    </div>
                </template>
            </VirtualizedTreeList>
        </div>
        <div id="person-tree-container" :class="{ 'is-loading': loading || graphLoading }">
            <div v-if="loading || graphLoading" class="person-tree-status person-tree-status--overlay">
                <LoadingSpinner />
                <span>{{ loading ? 'Personen laden...' : 'Layout laden...' }}</span>
                <div class="person-tree-progress">
                    <div class="person-tree-progress__bar" role="progressbar" :aria-valuemin="0" :aria-valuemax="100"
                        :aria-valuenow="loaderProgress">
                        <div class="person-tree-progress__fill" :style="{ width: `${loaderProgress}%` }"></div>
                    </div>
                    <span class="person-tree-progress__value">{{ loaderProgress }}%</span>
                </div>
            </div>
            <div v-else-if="error" class="person-tree-status person-tree-status--error">{{ error }}</div>
            <div v-else-if="hasNameFilters && nameFilterVisibleCount === 0"
                class="person-tree-status person-tree-status--empty">
                Geen personen gevonden voor deze naamfilter.
            </div>

        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';
import { markRaw } from 'vue';
import { fetchAllPeopleGraph, fetchAllPeopleGraphStaticFile } from '@/api/apiService';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { hasLivingPersonAccess, isLivingPerson, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';
import lockIcon from '@/assets/icons/lock_black.svg';
import VirtualizedTreeList from '@/components/VirtualizedTreeList.vue';
import {
    TREE_VIEW_STATE_SCHEMA_VERSION,
    TREE_DATA_SNAPSHOT_VERSION,
    getViewportWorldCenter,
    isCompatibleTreeViewState,
    restoreTransformFromWorldCenter,
    selectViewportLabels,
} from '@/utils/treePerformance.mjs';
import {
    buildAutomaticSurnameGroups,
    packLayoutRegionTargets,
    isUnlinkedResearchRegion,
    buildMolukseRegionConnectorTargets,
    buildSubcladeSurnameAffinityTargets,
    getCanonicalClusterSurname,
    normalizeClusterKey,
    resolveLayoutRegionAffinity,
} from '@/utils/personTreeClusters.mjs';
import {
    buildPersonNameFilterResult,
    normalizePersonName,
    parseNameFilters,
} from '@/utils/personTreeNameFilter.mjs';
import {
    buildParentalGatewaySubclade,
    buildSeparatedRadialTargets,
    buildUndirectedAdjacency,
    buildPersonRelationshipGraph,
    findLogicalRelationshipPath,
    partitionOverlappingSubclades,
    removeLivingOnlyComponents,
} from '@/utils/personTreeGraph.mjs';

import {
    PERSON_TREE_LAYOUT_VERSION, isCurrentPersonLayout, createLocalGroupForce, arrangeMeasuredGroups,
} from '@/utils/personTreeLayout.mjs';

const MAIN_SUBCLADE_VARIANT = 'main-subclade';
const MAIN_SUBCLADE_DEFINITIONS = [
    {
        id: 'theresia-margareta',
        anchorId: 'I1177',
        label: 'Eyang Widya',
        stopIds: ['I4361'],
    },
    { id: 'francis-duncan-jacobsz', anchorId: 'I15', label: 'Jacobsz' },
    { id: 'crabbe', anchorId: 'I21', label: 'Crabbe' },
    {
        id: 'wilhelmina-cornelia-cecilia-de-la-croix',
        anchorId: 'I98',
        label: 'de la Croix',
    },
    { id: 'notenboom', anchorId: 'I288', label: 'Notenboom' },
    { id: 'muller', anchorId: 'I304', label: 'Muller' },
    { id: 'alexandrien-frederique-westhoff', anchorId: 'I16', label: 'Westhoff' },
    { id: 'hetty-van-het-ende', anchorId: 'I42', label: 'van het Ende' },
    { id: 'cordie', anchorId: 'I294', label: 'Cordie' },
    { id: 'auliveld', anchorId: 'I387', label: 'Auliveld' },
    { id: 'mollet', anchorId: 'I12', label: 'Mollet' },
    {
        id: 'antoine-thumann',
        anchorId: 'I11',
        label: 'Thumann (Elzas)',
        surnameKey: 'thumann',
    },
    { id: 'akolo', anchorId: 'I10', label: 'Akolo' },
    { id: 'charles-jozef-thuemann', anchorId: 'I1', label: 'Thümann (Indië)' },
].map((definition, index, definitions) => ({
    ...definition,
    angle: (index * 2 * Math.PI) / definitions.length,
}));
const TATA_DNA_MATCH_VARIANT = 'tata-dna-match';
const TATA_DNA_MATCH_BRANCH_IDS = new Set(['B5', 'B6', 'B7']);
const NEIRINCKX_BRANCH_VARIANT = 'neirinckx';
const NEIRINCKX_BRANCH_IDS = new Set(['B8']);
const MOLUKSE_BRANCH_ID = 'B4';
const MOLUKSE_VISUAL_VARIANT = 'molukse';
const PRIVATE_LABEL_ICON_WIDTH = 10;
const PRIVATE_LABEL_ICON_HEIGHT = 12;
const PRIVATE_LABEL_ICON_PADDING = 2;
const ROUTE_COLOR = '#1768ac';

export default {
    name: 'PersonTree',
    components: {
        LoadingSpinner,
        VirtualizedTreeList,
    },
    props: {
        initialState: {
            type: Object,
            default: null,
        },
    },
    emits: ['state-change'],
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        return { authToken, localePath };
    },
    computed: {
        filteredNodeList() {
            const q = (this.searchQuery || '').trim().toLowerCase();
            if (!q) return this.nodeList;
            return this.nodeList.filter((node) => {
                const isPrivate = this.isPrivateNode(node);
                const label = isPrivate ? '' : (node.label || '').toLowerCase();
                const firstname = isPrivate ? '' : (node.firstname || '').toLowerCase();
                const lastname = isPrivate ? '' : (node.lastname || '').toLowerCase();
                const id = isPrivate ? '' : (node.id || '').toLowerCase();
                return label.includes(q) || firstname.includes(q) || lastname.includes(q) || id.includes(q);
            });
        },
        nameFilterResult() {
            const searchableNodes = this.rawNodes.filter(node => !this.isPrivateNode(node));
            return buildPersonNameFilterResult({
                nodes: searchableNodes,
                links: this.rawLinks,
                filters: this.nameFilters,
            });
        },
        hasNameFilters() {
            return this.nameFilters.length > 0;
        },
        nameFilterMatchedCount() {
            return this.nameFilterResult.matchedIds.size;
        },
        nameFilterVisibleCount() {
            return this.nameFilterResult.visibleIds?.size || 0;
        },
        loaderProgress() {
            if (this.loading && !this.graphLoading) return 5;
            return Math.max(0, Math.min(100, Math.round(this.graphProgress || 0)));
        },
        activePersonId() {
            return this.previewPerson || this.selectedPerson || '';
        },
        jeffreyTargetId() {
            const exactIdMatch = this.rawNodes.find(node => node.id === 'I43');
            if (exactIdMatch) return exactIdMatch.id;

            const normalize = (value) => (value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim();

            const jeffreyNode = this.rawNodes.find((node) => {
                const label = normalize(node.label);
                const firstName = normalize(node.firstname);
                const lastName = normalize(node.lastname);
                return label === 'jeffrey thumann'
                    || (firstName === 'jeffrey' && lastName === 'thumann');
            });

            return jeffreyNode?.id || '';
        },
        activePathSearch() {
            if (this.hasNameFilters || !this.activePersonId || !this.jeffreyTargetId) {
                return { routes: [] };
            }
            return this.findPathsToJeffrey(this.activePersonId);
        },
        relationshipGraph() {
            return buildPersonRelationshipGraph(this.rawNodes, this.rawLinks);
        },
        relationshipDetailsRoute() {
            const person = this.getPersonById(this.activePersonId);
            if (!person || !this.jeffreyTargetId || this.isPrivateNode(person) || person.id === this.jeffreyTargetId) return null;
            return {
                path: this.localePath(`/relationship/${this.jeffreyTargetId}/${person.id}`),
                query: { source: 'stamboom' },
            };
        },
        activePathResults() {
            return this.activePathSearch.routes;
        },
        activePathRoutes() {
            return this.activePathResults.map(result => result.path);
        },
        lockIconSrc() {
            return lockIcon;
        },
        privacyScope() {
            return hasLivingPersonAccess(this.authToken) ? 'member' : 'public';
        },
        legendItems() {
            return [
                { category: 'main', primaryKey: 'personTree.legend.mainPrimary', secondaryKey: 'personTree.legend.mainSecondary' },
                { category: 'claasz', primaryKey: 'personTree.legend.claaszPrimary', secondaryKey: 'personTree.legend.claaszSecondary' },
                { category: 'cluster', primaryKey: 'personTree.legend.clusterPrimary', secondaryKey: 'personTree.legend.clusterSecondary' },
                { category: 'molukse', primaryKey: 'personTree.legend.moluksePrimary', secondaryKey: 'personTree.legend.molukseSecondary' },
                { category: TATA_DNA_MATCH_VARIANT, primaryKey: 'personTree.legend.tataDnaMatchPrimary', secondaryKey: 'personTree.legend.tataDnaMatchSecondary' },
            ];
        },
    },
    data() {
        return {
            loading: true,
            error: null,
            graphLoading: false,
            graphProgress: 0,
            graphSimulationDone: false,
            graphFitDone: false,
            totalPeople: 0,
            rawNodes: [],
            rawLinks: [],
            nodeById: new Map(),
            nodeList: [],
            secondaryNodes: [],
            pathNodes: [],
            pathRoutes: [],
            selectedPerson: '',
            previewPerson: '',
            selectedPersonMeta: null,
            zoomBehavior: null,
            zoomSelection: null,
            simulation: null,
            simNodes: [],
            simWidth: 0,
            simHeight: 0,
            graphRoot: null,
            nodeSelection: null,
            linkSelection: null,
            labelSelection: null,
            highlightOverlaySelection: null,
            islandLabelSelection: null,
            islandLabelCenters: null,
            islandLabelFallbackCentroids: null,
            surnameGroupBoundarySelection: null,
            affinityConnectorSelection: null,
            componentMetaById: new Map(),
            mainSubclades: [],
            mainSubcladeByPersonId: new Map(),
            mainSubcladeAnchorIds: new Set(),
            renderableMainSubclades: [],
            surnameGroups: [],
            neirinckxBranchGroup: null,
            clusterSurnameLinks: [],
            labelMeasureCache: {},
            labelPlacementTick: 0,
            graphTickCount: 0,
            resizeTimer: null,
            viewportFitApplied: false,
            manualZoomDetected: false,
            applyingViewportFit: false,
            currentZoomK: 1,
            labelPriorityContext: {
                selectedId: '',
                targetId: null,
                secondaryIds: new Set(),
                pathIds: new Set(),
            },
            nodeDegreeMap: new Map(),
            layoutMode: 'cluster',
            labelMode: 'wrapped',
            labelSize: 8,
            overlapStrategy: 'hybrid',
            spacingPreset: 'spacious',
            searchQuery: '',
            nameFilterInput: '',
            nameFilters: [],
            hiddenLegendCategories: [],
            sidebarCollapsed: true,
            listScrollOffset: 0,
            isFullscreen: false,
            layoutSettled: false,
            positionRaf: null,
            resizeRaf: null,
            layoutCentroids: null,
            localLayoutGroups: null,
            measuredLayoutRegions: null,
        };
    },
    watch: {
        searchQuery() {
            if (this._restoringViewState) return;
            this.listScrollOffset = 0;
            this.$nextTick(() => this.$refs.personList?.scrollToKey(this.filteredNodeList[0]?.id, 'start'));
        },
    },
    mounted() {
        this.handleFullscreenChange();
        if (!this.restoreInitialState()) this.loadPeopleGraph();
        window.addEventListener('resize', this.resizeTree);
        window.addEventListener('click', this.closeDropdown);
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('msfullscreenchange', this.handleFullscreenChange);
    },
    beforeUnmount() {
        this.$emit('state-change', this.createStateSnapshot());
        if (this._zoomRaf) {
            cancelAnimationFrame(this._zoomRaf);
            this._zoomRaf = null;
        }
        window.removeEventListener('resize', this.resizeTree);
        window.removeEventListener('click', this.closeDropdown);
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('msfullscreenchange', this.handleFullscreenChange);
        this.teardownGraph();
    },
    methods: {
        restoreInitialState() {
            if (!isCompatibleTreeViewState(this.initialState, this.privacyScope, 'personen')) return false;
            const nodes = this.initialState?.data?.nodes;
            const links = this.initialState?.data?.links;
            if (!Array.isArray(nodes) || !Array.isArray(links) || !nodes.length) return false;

            this._restoringViewState = true;
            this.normalizeTreeData({ nodes, links });
            this.totalPeople = this.rawNodes.length;
            this.nodeList = [...this.rawNodes]
                .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'nl', { sensitivity: 'base' }));

            const view = this.initialState.view || {};
            this.selectedPerson = view.selection || '';
            this.selectedPersonMeta = this.getPersonById(this.selectedPerson);
            this.searchQuery = view.searchQuery || '';
            this.nameFilters = parseNameFilters(view.nameFilters || []);
            this.hiddenLegendCategories = Array.isArray(view.hiddenLegendCategories)
                ? view.hiddenLegendCategories.filter(category => this.legendItems.some(item => item.category === category))
                : [];
            this.sidebarCollapsed = view.sidebarCollapsed !== false;
            this.listScrollOffset = Number(view.scrollOffset) || 0;
            this.layoutMode = view.layoutMode || this.layoutMode;
            this.labelMode = view.labelMode || this.labelMode;
            this.labelSize = Number(view.labelSize) || this.labelSize;
            this.overlapStrategy = view.overlapStrategy || this.overlapStrategy;
            this.spacingPreset = view.spacingPreset || this.spacingPreset;
            this.computeSidebarDetails();

            this.loading = false;
            this.error = null;
            this.graphLoading = true;
            this.graphProgress = 8;
            this._pendingRestoreState = this.initialState;
            this.$nextTick(() => {
                this._restoringViewState = false;
                this.renderFullGraph(this.initialState);
            });
            return true;
        },
        createStateSnapshot() {
            if (!this.rawNodes.length) return null;
            const svgNode = this.zoomSelection?.node?.()
                || d3.select('#person-tree-container').select('svg').node();
            const transform = svgNode ? d3.zoomTransform(svgNode) : d3.zoomIdentity;
            return {
                schemaVersion: TREE_VIEW_STATE_SCHEMA_VERSION,
                privacyScope: this.privacyScope,
                data: {
                    snapshotVersion: TREE_DATA_SNAPSHOT_VERSION,
                    nodes: this.rawNodes.map(node => ({ ...node })),
                    links: this.rawLinks.map(link => ({
                        source: link.source?.id || link.source,
                        target: link.target?.id || link.target,
                        relation: link.relation,
                    })),
                    dataVersion: `${this.rawNodes.length}:${this.rawLinks.length}`,
                },
                layout: {
                    version: PERSON_TREE_LAYOUT_VERSION,
                    positions: this.simNodes.map(node => ({ id: node.id, x: node.x, y: node.y })),
                    settled: this.layoutSettled,
                    alpha: this.simulation?.alpha?.() || 0,
                },
                view: {
                    type: 'personen',
                    zoom: {
                        scale: transform.k,
                        worldCenter: getViewportWorldCenter(transform, this.simWidth, this.simHeight),
                    },
                    selection: this.selectedPerson,
                    searchQuery: this.searchQuery,
                    nameFilters: [...this.nameFilters],
                    hiddenLegendCategories: [...this.hiddenLegendCategories],
                    sidebarCollapsed: this.sidebarCollapsed,
                    scrollOffset: this.listScrollOffset,
                    layoutMode: this.layoutMode,
                    labelMode: this.labelMode,
                    labelSize: this.labelSize,
                    overlapStrategy: this.overlapStrategy,
                    spacingPreset: this.spacingPreset,
                    manualZoomDetected: this.manualZoomDetected,
                },
            };
        },
        async loadPeopleGraph() {
            this.loading = true;
            this.error = null;

            try {
                let data;
                if (hasLivingPersonAccess(this.authToken)) {
                    data = await fetchAllPeopleGraph();
                } else {
                    try {
                        data = await fetchAllPeopleGraphStaticFile();
                    } catch (staticError) {
                        console.warn('Static people graph unavailable; falling back to API.', staticError);
                        data = await fetchAllPeopleGraph();
                    }
                }
                this.normalizeTreeData(data);
                this.totalPeople = this.rawNodes.length;
                this.nodeList = [...this.rawNodes].sort((a, b) => a.label.localeCompare(b.label, 'nl', { sensitivity: 'base' }));
            } catch (error) {
                console.error('Error loading people graph:', error);
                this.error = 'De personenstructuur kon niet worden geladen.';
            } finally {
                this.loading = false;
                if (!this.error) {
                    this.graphProgress = 8;
                    this.graphSimulationDone = false;
                    this.graphFitDone = false;
                    this.graphLoading = true;
                    this.$nextTick(() => this.renderFullGraph());
                }
            }
        },
        normalizeTreeData(data) {
            const rawNodes = Array.isArray(data?.nodes) ? data.nodes : [];
            const rawLinks = Array.isArray(data?.links) ? data.links : [];

            let normalizedNodes = rawNodes
                .map((node) => {
                    const branchIds = this.normalizeBranchIds(node.branchIds || node.branchID || node.branch);
                    const id = node.personID || node.nodeID || node.id;
                    return {
                        id,
                        label: node.label || [node.firstname, node.lastname].filter(Boolean).join(' ').trim()
                            || (node.placeholder ? '' : id),
                        firstname: node.firstname || '',
                        lastname: node.lastname || '',
                        sex: node.sex || 'U',
                        living: isLivingPerson(node),
                        placeholder: Boolean(node.placeholder),
                        privacyStatus: node.privacyStatus || null,
                        birthdatetr: node.birthdatetr || null,
                        deathdatetr: node.deathdatetr || null,
                        photoPath: node.photoPath || null,
                        branchIds,
                        isMolukseBranch: this.hasMolukseBranch(branchIds),
                        isTataDnaMatch: this.hasTataDnaMatchBranch(branchIds),
                        isNeirinckxBranch: this.hasNeirinckxBranch(branchIds),
                    };
                })
                .filter(node => node.id);

            const validIds = new Set(normalizedNodes.map(node => node.id));
            let normalizedLinks = rawLinks
                .map((link) => {
                    const source = link.source || link.sourceID;
                    const target = link.target || link.targetID;
                    if (!source || !target) return null;
                    if (!validIds.has(source) || !validIds.has(target) || source === target) return null;
                    return {
                        source,
                        target,
                        relation: link.relation || link.relationType || 'related',
                    };
                })
                .filter(Boolean);

            if (!hasLivingPersonAccess(this.authToken)) {
                const publicGraph = removeLivingOnlyComponents(normalizedNodes, normalizedLinks);
                normalizedNodes = publicGraph.nodes;
                normalizedLinks = publicGraph.links;
            }

            this.rawNodes = normalizedNodes;
            this.rawLinks = normalizedLinks;
            this.nodeById = new Map(this.rawNodes.map(node => [node.id, node]));
            this.mainSubclades = MAIN_SUBCLADE_DEFINITIONS
                .map(definition => ({
                    ...definition,
                    ...buildParentalGatewaySubclade({
                        nodes: this.rawNodes,
                        links: this.rawLinks,
                        anchorId: definition.anchorId,
                        stopIds: definition.stopIds,
                        anchorIds: MAIN_SUBCLADE_DEFINITIONS.map(item => item.anchorId),
                        includeAnchorHouseholds: true,
                    }),
                }))
                .filter(subclade => subclade.memberIds.size > 1);

            this.computeGenerations();
            this.computeComponents();
            this.rebuildMainSubcladeIndexes();
            this.computeNodeDegrees();
            this.componentMetaById = this.buildComponentMetadata();
            this.surnameGroups = this.buildSurnameGroups(this.componentMetaById);
            this.neirinckxBranchGroup = this.buildNeirinckxBranchGroup(this.componentMetaById);
            this.clusterSurnameLinks = this.buildClusterSurnameLinks();
        },
        normalizeBranchIds(value) {
            const rawValues = Array.isArray(value)
                ? value
                : String(value || '').split(',');
            return [...new Set(rawValues
                .map(branch => String(branch || '').trim())
                .filter(Boolean))];
        },
        hasMolukseBranch(branchIds) {
            return this.normalizeBranchIds(branchIds)
                .some(branchId => branchId.toUpperCase() === MOLUKSE_BRANCH_ID);
        },
        hasTataDnaMatchBranch(branchIds) {
            return this.normalizeBranchIds(branchIds)
                .some(branchId => TATA_DNA_MATCH_BRANCH_IDS.has(branchId.toUpperCase()));
        },
        hasNeirinckxBranch(branchIds) {
            return this.normalizeBranchIds(branchIds)
                .some(branchId => NEIRINCKX_BRANCH_IDS.has(branchId.toUpperCase()));
        },
        pickInitialPerson() {
            const rootCandidate = this.rawNodes.find(node => /thümann|thumann/i.test(node.lastname) || /thümann|thumann/i.test(node.label));
            return rootCandidate?.id || this.rawNodes[0]?.id || '';
        },
        getPersonById(personId) {
            return this.nodeById.get(personId) || null;
        },
        buildAdjacency() {
            return buildUndirectedAdjacency(this.rawNodes, this.rawLinks);
        },
        nameFilterMatchCount(filter) {
            return this.nameFilterResult.matchesByFilter.get(normalizePersonName(filter))?.size || 0;
        },
        addNameFilters() {
            const additions = parseNameFilters(this.nameFilterInput);
            if (!additions.length) return;

            this.nameFilters = parseNameFilters([...this.nameFilters, ...additions]);
            this.nameFilterInput = '';
            this.applyNameFilters();
        },
        removeNameFilter(filter) {
            const key = normalizePersonName(filter);
            this.nameFilters = this.nameFilters.filter(item => normalizePersonName(item) !== key);
            this.applyNameFilters();
        },
        clearNameFilters() {
            this.nameFilters = [];
            this.applyNameFilters();
        },
        isVisibleByNameFilter(nodeOrId) {
            if (!this.hasNameFilters) return true;
            const id = typeof nodeOrId === 'object' ? nodeOrId?.id : nodeOrId;
            return this.nameFilterResult.visibleIds?.has(id) || false;
        },
        getMainSubclade(nodeOrId) {
            const id = typeof nodeOrId === 'object' ? nodeOrId?.id : nodeOrId;
            if (!id) return null;
            return this.mainSubcladeByPersonId.get(id) || null;
        },
        isMainSubcladeNode(nodeOrId) {
            return Boolean(this.getMainSubclade(nodeOrId));
        },
        rebuildMainSubcladeIndexes() {
            const anchorIds = new Set();
            const fullSubclades = [];

            for (const subclade of this.mainSubclades) {
                const memberIds = [];
                for (const personId of subclade.memberIds) {
                    if (this.nodeById.get(personId)?.componentId === 0) memberIds.push(personId);
                }
                if (memberIds.length > 1) {
                    fullSubclades.push({ ...subclade, memberIds });
                }
            }

            const renderable = partitionOverlappingSubclades({
                subclades: fullSubclades,
                links: this.rawLinks,
                nodes: this.rawNodes,
            })
                .filter(subclade => subclade.memberIds.length > 1);
            renderable.forEach(subclade => anchorIds.add(subclade.anchorId));
            const byPersonId = new Map();
            for (const subclade of renderable) {
                subclade.memberIds.forEach(personId => byPersonId.set(personId, subclade));
            }

            this.mainSubcladeByPersonId = byPersonId;
            this.mainSubcladeAnchorIds = anchorIds;
            this.renderableMainSubclades = renderable;
        },
        getRenderableMainSubclades() {
            return this.renderableMainSubclades;
        },
        getNodeLegendCategory(nodeOrId) {
            const node = typeof nodeOrId === 'object' ? nodeOrId : this.getPersonById(nodeOrId);
            const meta = this.componentMetaById.get(node?.componentId);
            if (meta?.variant === 'main') return 'main';
            if (meta?.visualVariant === MOLUKSE_VISUAL_VARIANT) return MOLUKSE_VISUAL_VARIANT;
            if (meta?.visualVariant === TATA_DNA_MATCH_VARIANT || meta?.variant === TATA_DNA_MATCH_VARIANT) {
                return TATA_DNA_MATCH_VARIANT;
            }
            if (meta?.variant === 'claasz') return 'claasz';
            return 'cluster';
        },
        getLabelLegendCategory(labelData) {
            if (labelData?.variant === MAIN_SUBCLADE_VARIANT) return 'main';
            const componentId = labelData?.componentIds?.[0] ?? labelData?.componentId;
            const meta = this.componentMetaById.get(componentId);
            if (meta) return this.getNodeLegendCategory({ componentId });
            if (labelData?.visualVariant === MOLUKSE_VISUAL_VARIANT) return MOLUKSE_VISUAL_VARIANT;
            if (labelData?.visualVariant === TATA_DNA_MATCH_VARIANT) return TATA_DNA_MATCH_VARIANT;
            if (labelData?.variant === 'claasz') return 'claasz';
            return 'cluster';
        },
        isLegendCategoryHidden(category) {
            return this.hiddenLegendCategories.includes(category);
        },
        isVisibleByLegendFilter(nodeOrId) {
            return !this.isLegendCategoryHidden(this.getNodeLegendCategory(nodeOrId));
        },
        isLabelVisibleByLegendFilter(labelData) {
            return !this.isLegendCategoryHidden(this.getLabelLegendCategory(labelData));
        },
        isNodeRendered(nodeOrId) {
            return this.isVisibleByNameFilter(nodeOrId) && this.isVisibleByLegendFilter(nodeOrId);
        },
        isLinkRendered(link) {
            const sourceId = link?.source?.id || link?.source;
            const targetId = link?.target?.id || link?.target;
            return this.isNodeRendered(sourceId) && this.isNodeRendered(targetId);
        },
        applyTreeFilterVisibility() {
            this.nodeSelection?.attr('display', node => this.isNodeRendered(node) ? null : 'none');
            this.linkSelection?.attr('display', link => this.isLinkRendered(link) ? null : 'none');
            this.graphRoot?.select('.component-labels').style('display', this.hasNameFilters ? 'none' : null);
            this.graphRoot?.select('.surname-group-boundaries').style('display', this.hasNameFilters ? 'none' : null);
            this.graphRoot?.select('.layout-affinity-connectors')
                .style('display', this.hasNameFilters || this.activePersonId ? 'none' : null);
            if (!this.hasNameFilters) {
                this.islandLabelSelection?.attr('display', data => (
                    this.isLabelVisibleByLegendFilter(data) ? null : 'none'
                ));
                this.surnameGroupBoundarySelection?.attr('display', data => (
                    this.isLabelVisibleByLegendFilter(data) ? null : 'none'
                ));
                this.affinityConnectorSelection?.attr('display', data => (
                    !this.isLegendCategoryHidden('main') && this.isLabelVisibleByLegendFilter(data)
                        ? null
                        : 'none'
                ));
            }
        },
        applyNameFilterVisibility() {
            this.applyTreeFilterVisibility();
        },
        toggleLegendCategory(category) {
            if (this.isLegendCategoryHidden(category)) {
                this.hiddenLegendCategories = this.hiddenLegendCategories.filter(item => item !== category);
            } else {
                this.hiddenLegendCategories = [...this.hiddenLegendCategories, category];
            }

            if (this.selectedPerson && !this.isNodeRendered(this.selectedPerson)) {
                this.selectedPerson = '';
                this.previewPerson = '';
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.pathRoutes = [];
                this.selectedPersonMeta = null;
            }
            this.clearHighlightOverlay();
            this.recalculateFilteredLayout();
            this.highlightPersonInGraph();
        },
        getLayoutNodes() {
            return this.simNodes.filter(node => this.isNodeRendered(node));
        },
        getLayoutLinks() {
            return (this.linkSelection?.data() || []).filter(link => this.isLinkRendered(link));
        },
        recalculateFilteredLayout() {
            if (this.layoutMode === 'cluster') {
                this.applyTreeFilterVisibility();
                return;
            }
            if (!this.simulation) {
                this.applyTreeFilterVisibility();
                return;
            }
            const layoutNodes = this.getLayoutNodes();
            const layoutLinks = this.getLayoutLinks();
            this.simulation.nodes(layoutNodes);
            this.simulation.force('link')?.links(layoutLinks);
            this.viewportFitApplied = false;
            this.manualZoomDetected = false;
            this.applyTreeFilterVisibility();
            if (layoutNodes.length) this.applyLayout(this.layoutMode);
        },
        applyNameFilters() {
            if (this.selectedPerson && !this.isNodeRendered(this.selectedPerson)) {
                this.selectedPerson = '';
                this.previewPerson = '';
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.pathRoutes = [];
                this.selectedPersonMeta = null;
            }

            this.clearHighlightOverlay();
            this.recalculateFilteredLayout();
            this.highlightPersonInGraph();
            this.refreshLabels();
            this.listScrollOffset = 0;
            this.$nextTick(() => this.$refs.personList?.scrollToKey(this.filteredNodeList[0]?.id, 'start'));
        },
        computeGenerations() {
            // BFS following parent links (source=parentID → target=childID).
            // Root ancestors = nodes that never appear as a child (no incoming parent link).
            const childIds = new Set(
                this.rawLinks
                    .filter(l => l.relation === 'parent')
                    .map(l => l.target)
            );
            // Build parent→children map
            const childrenOf = new Map();
            for (const l of this.rawLinks) {
                if (l.relation !== 'parent') continue;
                if (!childrenOf.has(l.source)) childrenOf.set(l.source, []);
                childrenOf.get(l.source).push(l.target);
            }

            const generation = new Map();
            const queue = [];
            for (const node of this.rawNodes) {
                if (!childIds.has(node.id)) {
                    generation.set(node.id, 0);
                    queue.push(node.id);
                }
            }
            while (queue.length) {
                const id = queue.shift();
                const gen = generation.get(id);
                for (const childId of (childrenOf.get(id) || [])) {
                    if (!generation.has(childId)) {
                        generation.set(childId, gen + 1);
                        queue.push(childId);
                    }
                }
            }

            for (const node of this.rawNodes) {
                node.generation = generation.has(node.id) ? generation.get(node.id) : -1;
            }
        },
        computeComponents() {
            // Union-Find across all links.
            const parent = new Map();
            const rank = new Map();
            const find = (x) => {
                if (parent.get(x) !== x) parent.set(x, find(parent.get(x)));
                return parent.get(x);
            };
            const union = (a, b) => {
                const ra = find(a), rb = find(b);
                if (ra === rb) return;
                if ((rank.get(ra) || 0) < (rank.get(rb) || 0)) { parent.set(ra, rb); }
                else if ((rank.get(ra) || 0) > (rank.get(rb) || 0)) { parent.set(rb, ra); }
                else { parent.set(rb, ra); rank.set(ra, (rank.get(ra) || 0) + 1); }
            };
            for (const node of this.rawNodes) { parent.set(node.id, node.id); rank.set(node.id, 0); }
            for (const l of this.rawLinks) { union(l.source, l.target); }

            // Assign stable integer component IDs sorted by component size desc
            const compMap = new Map(); // root → [nodeId, ...]
            for (const node of this.rawNodes) {
                const root = find(node.id);
                if (!compMap.has(root)) compMap.set(root, []);
                compMap.get(root).push(node.id);
            }
            const sorted = [...compMap.values()].sort((a, b) => b.length - a.length);
            const idOf = new Map();
            sorted.forEach((members, idx) => members.forEach(id => idOf.set(id, idx)));
            for (const node of this.rawNodes) {
                node.componentId = idOf.get(node.id) ?? 0;
            }
            // Store component sizes for use in applyLayout
            this._componentSizes = sorted.map(m => m.length);
        },
        computeNodeDegrees() {
            const degree = new Map();
            for (const n of this.rawNodes) degree.set(n.id, 0);
            for (const l of this.rawLinks) {
                degree.set(l.source, (degree.get(l.source) || 0) + 1);
                degree.set(l.target, (degree.get(l.target) || 0) + 1);
            }
            this.nodeDegreeMap = degree;
        },
        computeBaseComponentCentroids(width, height, spreadFactor = 1) {
            // Largest component (id=0) → canvas center.
            // Others arranged in a spiral/grid around it.
            const sizes = this._componentSizes || [];
            const count = sizes.length;
            const cx = width / 2;
            const cy = height / 2;
            const centroids = new Map();
            centroids.set(0, { x: cx, y: cy });
            if (count <= 1) return centroids;

            // Estimate area needed per component, place in a sunflower spiral
            const goldenAngle = Math.PI * (3 - Math.sqrt(5));
            let spiralIdx = 0;
            for (let i = 1; i < count; i++) {
                spiralIdx++;
                const angle = spiralIdx * goldenAngle;
                // Distance from center proportional to index; smaller components stay closer
                const radius = (Math.min(width, height) * 0.35 + spiralIdx * 30) * spreadFactor;
                centroids.set(i, {
                    x: cx + radius * Math.cos(angle),
                    y: cy + radius * Math.sin(angle),
                });
            }
            return centroids;
        },
        getMainCoreLayoutRadius(spreadFactor = 1) {
            const mainSize = this.componentMetaById.get(0)?.size || this._componentSizes?.[0] || 1;
            const assignedSubcladeCount = this.mainSubcladeByPersonId?.size || 0;
            const corePeopleCount = Math.max(0, mainSize - assignedSubcladeCount);
            return Math.max(96, Math.sqrt(corePeopleCount) * 7.5) * spreadFactor;
        },
        getComponentLayoutRadius(componentId, spreadFactor = 1) {
            const meta = this.componentMetaById.get(componentId);
            const size = meta?.size || this._componentSizes?.[componentId] || 1;
            const base = Math.max(30, Math.sqrt(size) * 11);
            if (meta?.variant === 'main') {
                const coreRadius = this.getMainCoreLayoutRadius(spreadFactor);
                return buildSeparatedRadialTargets({
                    subclades: this.getRenderableMainSubclades(),
                    baseRadius: 390 * spreadFactor,
                    innerRadius: coreRadius,
                    padding: 48 * spreadFactor,
                    radiusPerMember: 12 * spreadFactor,
                    minimumGroupRadius: 68 * spreadFactor,
                }).outerRadius;
            }
            if (meta?.variant === 'claasz') return Math.max(96, Math.sqrt(size) * 16) * spreadFactor;
            return base * spreadFactor;
        },
        buildLayoutRegions(spreadFactor = 1) {
            const groupedComponentIds = new Set();
            const regions = [];

            const mainMeta = this.componentMetaById.get(0);
            regions.push({
                id: 'main',
                type: 'main',
                componentIds: [0],
                peopleCount: mainMeta?.size || this._componentSizes?.[0] || 1,
                radius: this.getComponentLayoutRadius(0, spreadFactor),
                fixed: true,
            });

            for (const group of this.surnameGroups || []) {
                group.componentIds.forEach(id => groupedComponentIds.add(id));
                const innerRadius = Math.max(92, Math.sqrt(group.peopleCount || group.componentIds.length) * 15);
                const componentAllowance = Math.max(48, group.componentIds.length * 18);
                regions.push({
                    id: `surname:${group.groupKey || group.surnameKey}`,
                    type: 'surname',
                    surnameKey: group.surnameKey,
                    surname: group.surname,
                    surnameKeys: [...new Set(group.componentIds.flatMap(componentId => (
                        this.componentMetaById.get(componentId)?.surnameKeys || []
                    )))],
                    componentIds: [...group.componentIds],
                    peopleCount: group.peopleCount,
                    radius: (innerRadius + componentAllowance) * spreadFactor,
                    variant: 'surname-group',
                    visualVariant: group.visualVariant,
                });
            }

            const neirinckxGroup = this.neirinckxBranchGroup;
            if (neirinckxGroup?.componentIds?.length) {
                neirinckxGroup.componentIds.forEach(id => groupedComponentIds.add(id));
                const innerRadius = Math.max(110, Math.sqrt(neirinckxGroup.peopleCount || neirinckxGroup.componentIds.length) * 15);
                const componentAllowance = Math.max(58, neirinckxGroup.componentIds.length * 20);
                regions.push({
                    id: NEIRINCKX_BRANCH_VARIANT,
                    type: NEIRINCKX_BRANCH_VARIANT,
                    componentIds: [...neirinckxGroup.componentIds],
                    peopleCount: neirinckxGroup.peopleCount,
                    radius: (innerRadius + componentAllowance) * spreadFactor,
                });
            }

            for (const meta of this.componentMetaById.values()) {
                if (meta.componentId === 0 || groupedComponentIds.has(meta.componentId)) continue;
                // Keep disconnected Java/Batak DNA-match branches as separate islands.
                // Their component metadata still supplies the shared visual treatment.
                regions.push({
                    id: `component:${meta.componentId}`,
                    type: meta.variant === 'claasz' ? 'claasz' : 'single',
                    surnameKey: meta.surnameKey,
                    surname: meta.surname,
                    surnameKeys: meta.surnameKeys,
                    componentIds: [meta.componentId],
                    peopleCount: meta.size,
                    radius: this.getComponentLayoutRadius(meta.componentId, spreadFactor),
                    variant: meta.variant,
                    visualVariant: meta.visualVariant,
                });
            }

            const subcladeSurnameTargets = buildSubcladeSurnameAffinityTargets(
                this.getRenderableMainSubclades(),
            );
            const resolvedRegions = regions.map((region) => ({
                ...region,
                ...(resolveLayoutRegionAffinity(region, { subcladeSurnameTargets }) || {}),
            }));
            const molukseConnectorTargets = buildMolukseRegionConnectorTargets({
                regions: resolvedRegions,
                surnameLinks: this.clusterSurnameLinks,
            });
            return resolvedRegions.map((region) => ({
                ...region,
                ...(molukseConnectorTargets.get(region.id) || {}),
                ...(molukseConnectorTargets.has(region.id) ? {
                    affinityConnector: true,
                    affinityKind: 'cultural',
                } : {}),
            })).sort((a, b) => {
                if (a.type === 'main') return -1;
                if (b.type === 'main') return 1;
                if (a.type === 'claasz') return -1;
                if (b.type === 'claasz') return 1;
                if (a.type === NEIRINCKX_BRANCH_VARIANT) return -1;
                if (b.type === NEIRINCKX_BRANCH_VARIANT) return 1;
                if (a.type === 'surname' && b.type !== 'surname') return -1;
                if (b.type === 'surname' && a.type !== 'surname') return 1;
                return b.radius - a.radius || a.id.localeCompare(b.id);
            });
        },
        packLayoutRegions(width, height, spreadFactor = 1) {
            const regions = this.buildLayoutRegions(spreadFactor);
            if (!regions.length) return regions;

            const cx = width / 2;
            const cy = height / 2;
            const mainRadius = regions.find(region => region.type === 'main')?.radius || 260;
            const targetAngles = new Map(this.getRenderableMainSubclades()
                .map(subclade => [subclade.id, subclade.angle]));
            const homeTargets = packLayoutRegionTargets({
                regions, centerX: cx, centerY: cy, mainRadius, spreadFactor, targetAngles,
            });
            return regions.map(region => ({ ...region, ...homeTargets.get(region.id) }));
        },
        computeComponentCentroids(width, height, spreadFactor = 1) {
            const regions = this.packLayoutRegions(width, height, spreadFactor);
            const centroids = new Map();
            const goldenAngle = Math.PI * (3 - Math.sqrt(5));

            for (const region of regions) {
                const ids = [...region.componentIds].sort((a, b) => {
                    const sizeA = this.componentMetaById.get(a)?.size || 0;
                    const sizeB = this.componentMetaById.get(b)?.size || 0;
                    return sizeB - sizeA || a - b;
                });

                if (ids.length === 1) {
                    centroids.set(ids[0], { x: region.x, y: region.y });
                    continue;
                }

                const innerRadius = Math.max(56, region.radius * 0.32);
                const startAngle = this.normalizeSurnameKey(region.surnameKey || region.id).split('')
                    .reduce((sum, char) => sum + char.charCodeAt(0), 0) * 0.017;

                ids.forEach((componentId, idx) => {
                    const angle = startAngle + idx * goldenAngle;
                    const size = this.componentMetaById.get(componentId)?.size || 1;
                    const radialJitter = Math.min(22, Math.sqrt(size) * 2.6);
                    const radius = innerRadius + radialJitter;
                    centroids.set(componentId, {
                        x: region.x + radius * Math.cos(angle),
                        y: region.y + radius * Math.sin(angle),
                    });
                });
            }

            return centroids;
        },
        computeComponentBounds() {
            const bounds = new Map();
            for (const node of this.simNodes) {
                const componentId = node.componentId ?? 0;
                if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) continue;
                if (!bounds.has(componentId)) {
                    bounds.set(componentId, {
                        minX: node.x,
                        maxX: node.x,
                        minY: node.y,
                        maxY: node.y,
                        count: 0,
                    });
                }
                const bucket = bounds.get(componentId);
                bucket.minX = Math.min(bucket.minX, node.x);
                bucket.maxX = Math.max(bucket.maxX, node.x);
                bucket.minY = Math.min(bucket.minY, node.y);
                bucket.maxY = Math.max(bucket.maxY, node.y);
                bucket.count += 1;
            }
            return bounds;
        },
        computeLiveComponentCenters() {
            const bounds = this.computeComponentBounds();

            const output = new Map();
            for (const [componentId, bucket] of bounds.entries()) {
                output.set(componentId, {
                    x: (bucket.minX + bucket.maxX) / 2,
                    y: (bucket.minY + bucket.maxY) / 2,
                });
            }
            return output;
        },
        getSpacingPresetConfig() {
            if (this.spacingPreset === 'dense') {
                return {
                    linkDistance: 50,
                    linkStrength: 0.52,
                    charge: -50,
                    collide: 5,
                    rowMultiplier: 0.85,
                    radialFactor: 0.36,
                    radialStrength: 0.45,
                    clusterPull: 0.22,
                    clusterSpread: 1,
                };
            }
            if (this.spacingPreset === 'spacious') {
                return {
                    linkDistance: 100,
                    linkStrength: 0.44,
                    charge: -150,
                    collide: 13,
                    rowMultiplier: 1.22,
                    radialFactor: 0.58,
                    radialStrength: 0.55,
                    clusterPull: 0.32,
                    clusterSpread: 1.45,
                };
            }
            return {
                linkDistance: 64,
                linkStrength: 0.5,
                charge: -72,
                collide: 7,
                rowMultiplier: 1,
                radialFactor: 0.45,
                radialStrength: 0.5,
                clusterPull: 0.24,
                clusterSpread: 1.2,
            };
        },
        buildLocalLayoutGroups() {
            const regions = this.buildLayoutRegions(this.getSpacingPresetConfig().clusterSpread);
            const regionByComponent = new Map(regions.flatMap(region => region.componentIds.map(id => [id, region])));
            const groups = new Map();
            for (const node of this.simNodes) {
                const subclade = node.componentId === 0 ? this.getMainSubclade(node) : null;
                const region = regionByComponent.get(node.componentId);
                const id = subclade ? `subclade:${subclade.id}` : node.componentId === 0 ? 'core' : region?.id;
                if (!id) continue;
                if (!groups.has(id)) groups.set(id, {
                    id, subcladeId: subclade?.id, label: subclade?.label || region?.surname || '',
                    padding: node.componentId === 0 ? 40 : 112, encloseRectangle: node.componentId !== 0, nodes: [],
                });
                groups.get(id).nodes.push(node);
            }
            return [...groups.values()];
        },
        arrangeClusterGroups() {
            if (this.layoutMode !== 'cluster' || !this.localLayoutGroups) return;
            const arranged = arrangeMeasuredGroups({
                groups: this.localLayoutGroups, subclades: this.getRenderableMainSubclades(),
                regions: this.buildLayoutRegions(this.getSpacingPresetConfig().clusterSpread),
                centerX: this.simWidth / 2, centerY: this.simHeight / 2,
                spreadFactor: this.getSpacingPresetConfig().clusterSpread,
            });
            this.localLayoutGroups = markRaw(arranged.groups);
            this.measuredLayoutRegions = markRaw(arranged.regions);
            this.layoutCentroids = new Map(arranged.regions.flatMap(region => region.componentIds.map(id => [id, region])));
        },
        applyLayout(mode) {
            if (!this.simulation) return;
            const width = this.simWidth;
            const height = this.simHeight;
            const cx = width / 2;
            const cy = height / 2;
            const layoutNodes = mode === 'cluster' ? this.simNodes : this.getLayoutNodes();
            const spacing = this.getSpacingPresetConfig();
            this.layoutSettled = false;
            this.graphSimulationDone = false;
            this.labelSelection = null;
            this.islandLabelSelection = null;
            this.surnameGroupBoundarySelection = null;
            this.affinityConnectorSelection = null;
            this.graphRoot?.select('.labels')?.selectAll('*').remove();
            this.graphRoot?.select('.component-labels')?.selectAll('*').remove();
            this.graphRoot?.select('.surname-group-boundaries')?.selectAll('*').remove();
            this.graphRoot?.select('.layout-affinity-connectors')?.selectAll('*').remove();
            this.islandLabelCenters = null;
            this.islandLabelFallbackCentroids = null;
            this.layoutCentroids = null;

            if (this.simulation.nodes().length !== layoutNodes.length) {
                this.simulation.nodes(layoutNodes);
                this.simulation.force('link')?.links(this.getLayoutLinks());
            }
            const linkForce = this.simulation.force('link');
            if (linkForce) {
                linkForce
                    .distance(spacing.linkDistance)
                    .strength(spacing.linkStrength);
            }
            this.simulation
                .force('charge', d3.forceManyBody().strength(spacing.charge))
                .force('collide', d3.forceCollide(spacing.collide));

            // Clear all layout-specific forces
            this.simulation
                .force('center', null)
                .force('forceY', null)
                .force('forceX', null)
                .force('radial', null)
                .force('cluster', null)
                .force('subclade-separation', null)
                .force('group-local', null);

            const gens = layoutNodes.map(n => n.generation).filter(g => g >= 0);
            const maxGen = gens.length ? Math.max(...gens) : 0;
            const rowSpacing = Math.min(95, (height * 0.85) / Math.max(maxGen, 1)) * spacing.rowMultiplier;

            if (mode === 'generational') {
                this.simulation
                    .force('forceY', d3.forceY(d => {
                        const g = d.generation >= 0 ? d.generation : maxGen + 1;
                        return g * rowSpacing + 60;
                    }).strength(0.6))
                    .force('forceX', d3.forceX(cx).strength(0.04));

            } else if (mode === 'cluster') {
                this.localLayoutGroups = markRaw(this.buildLocalLayoutGroups());
                this.measuredLayoutRegions = null;
                for (const group of this.localLayoutGroups) {
                    const x = group.nodes.reduce((sum, node) => sum + node.x, 0) / group.nodes.length;
                    const y = group.nodes.reduce((sum, node) => sum + node.y, 0) / group.nodes.length;
                    for (const node of group.nodes) {
                        node.x -= x;
                        node.y -= y;
                        node.vx = 0;
                        node.vy = 0;
                    }
                }
                this.simulation
                    .force('link', d3.forceLink([]).id(node => node.id))
                    .force('charge', null)
                    .force('collide', null)
                    .force('group-local', createLocalGroupForce(this.localLayoutGroups, this.rawLinks, spacing));

            } else if (mode === 'radial') {
                const radialStep = Math.min(110, (Math.min(width, height) * spacing.radialFactor) / Math.max(maxGen, 1));
                this.simulation.force('radial', d3.forceRadial(d => {
                    const g = d.generation >= 0 ? d.generation : maxGen + 1;
                    return g * radialStep + 80;
                }, cx, cy).strength(spacing.radialStrength));
            }

            // Reset viewport culling so all nodes are visible while simulation re-runs
            this.applyTreeFilterVisibility();
            this.simulation.alpha(0.8).restart();
        },
        fitGraphToViewport(force = false) {
            if (!this.zoomBehavior || !this.simNodes.length || !this.graphRoot) return false;
            if (this.manualZoomDetected && !force) return false;

            const positions = this.simNodes.filter(node => this.isNodeRendered(node)
                && Number.isFinite(node.x) && Number.isFinite(node.y));
            if (!positions.length) return false;

            const xs = positions.map(node => node.x);
            const ys = positions.map(node => node.y);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);

            const boundsWidth = Math.max(maxX - minX, 1);
            const boundsHeight = Math.max(maxY - minY, 1);
            const container = d3.select('#person-tree-container').node();
            if (!container) return false;

            const containerWidth = container.offsetWidth || this.simWidth;
            const containerHeight = container.offsetHeight || this.simHeight;
            const padding = 120;

            const scaleX = (containerWidth - padding * 2) / boundsWidth;
            const scaleY = (containerHeight - padding * 2) / boundsHeight;
            const scale = Math.max(Math.min(Math.min(scaleX, scaleY), 1.35), 0.01);

            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const translateX = containerWidth / 2 - midX * scale;
            const translateY = containerHeight / 2 - midY * scale;

            const svg = d3.select('#person-tree-container').select('svg');
            if (svg.empty()) return false;

            this.applyingViewportFit = true;
            this.graphProgress = Math.max(this.graphProgress, 92);
            svg.transition()
                .duration(450)
                .call(this.zoomBehavior.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale))
                .on('end', () => {
                    this.applyingViewportFit = false;
                    this.graphFitDone = true;
                    this.refreshLabels();
                    this.applyIslandLabelTransforms();
                    this._applyViewportCulling();
                    this.markGraphLoadingComplete();
                })
                .on('interrupt', () => {
                    this.applyingViewportFit = false;
                    this.graphFitDone = true;
                    this.refreshLabels();
                    this.applyIslandLabelTransforms();
                    this._applyViewportCulling();
                    this.markGraphLoadingComplete();
                });

            this.viewportFitApplied = true;
            return true;
        },
        markGraphLoadingComplete() {
            if (!this.graphLoading) return;
            if (this.graphSimulationDone && this.graphFitDone) {
                this.graphProgress = 100;
                requestAnimationFrame(() => {
                    if (this.graphSimulationDone && this.graphFitDone) {
                        this.graphLoading = false;
                    }
                });
            }
        },
        onReadabilityChanged() {
            this.applyLayout(this.layoutMode);
            this.highlightPersonInGraph();
        },
        isPrivateNode(node) {
            return isLivingPlaceholder(node) || isPrivateLivingPerson(node, this.authToken);
        },
        isPrivateLabel(node) {
            return this.isPrivateNode(node);
        },
        privateLabelTitle(node) {
            if (isLivingPlaceholder(node)) return this.$t('privacy.livingPlaceholder');
            return this.$t('home.nameHiddenPrivacy');
        },
        getDisplayName(node) {
            if (!node) return '';
            if (this.isPrivateLabel(node)) return this.privateLabelTitle(node);
            return node.label || '';
        },
        formatLabelLines(node) {
            if (this.isPrivateLabel(node)) {
                return [];
            }

            const fullLabel = node.label || '';
            if (this.labelMode === 'full') {
                return [fullLabel];
            }

            const firstname = (node.firstname || '').trim();
            const lastname = (node.lastname || '').trim();
            if (this.labelMode === 'abbreviated') {
                if (!firstname && !lastname) return [fullLabel];
                const initials = firstname
                    .split(/\s+/)
                    .filter(Boolean)
                    .map(part => `${part[0]}.`)
                    .join(' ');
                const compact = [initials, lastname].filter(Boolean).join(' ').trim();
                return [compact || fullLabel];
            }

            if (!firstname && !lastname) {
                const fallbackTokens = fullLabel.split(/\s+/).filter(Boolean);
                if (fallbackTokens.length <= 1) return [fullLabel];
                return [fallbackTokens.slice(0, -1).join(' '), fallbackTokens.slice(-1).join(' ')];
            }

            return [firstname || fullLabel, lastname || ''];
        },
        getAnchorMap() {
            const size = this.labelSize;
            const near = size + 5;
            const far = size + 11;
            return {
                E: { key: 'E', dx: near, dy: 0, anchor: 'start', lx: near - 2, ly: 0 },
                W: { key: 'W', dx: -near, dy: 0, anchor: 'end', lx: -near + 2, ly: 0 },
                N: { key: 'N', dx: 0, dy: -far, anchor: 'middle', lx: 0, ly: -far + 3 },
                S: { key: 'S', dx: 0, dy: far, anchor: 'middle', lx: 0, ly: far - 3 },
                NE: { key: 'NE', dx: near, dy: -near, anchor: 'start', lx: near - 2, ly: -near + 2 },
                NW: { key: 'NW', dx: -near, dy: -near, anchor: 'end', lx: -near + 2, ly: -near + 2 },
                SE: { key: 'SE', dx: near, dy: near, anchor: 'start', lx: near - 2, ly: near - 2 },
                SW: { key: 'SW', dx: -near, dy: near, anchor: 'end', lx: -near + 2, ly: near - 2 },
            };
        },
        getAnchorCandidates(node) {
            const anchors = this.getAnchorMap();
            const keys = Object.keys(anchors);
            const cx = this.simWidth / 2;
            const cy = this.simHeight / 2;
            const dx = (node.x || 0) - cx;
            const dy = (node.y || 0) - cy;
            const angle = Math.atan2(dy, dx);
            const sectors = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];
            const sectorIndex = Math.round(((angle + Math.PI) / (2 * Math.PI)) * 7) % 8;
            const radialAnchor = sectors[sectorIndex];
            const horizontal = dx < 0 ? 'W' : 'E';
            const vertical = dy < 0 ? 'N' : 'S';
            const quadrantAnchor = `${vertical}${horizontal}`;
            const directionAnchor = Math.abs(dx) > Math.abs(dy) ? horizontal : vertical;

            const hashSeed = String(node.id || '0');
            let hash = 0;
            for (let i = 0; i < hashSeed.length; i++) hash = ((hash << 5) - hash) + hashSeed.charCodeAt(i);
            const rotatedKeys = [...keys.slice(Math.abs(hash) % keys.length), ...keys.slice(0, Math.abs(hash) % keys.length)];

            let priority;
            if (this.overlapStrategy === 'quadrant') {
                priority = [quadrantAnchor, directionAnchor, radialAnchor, 'E', 'W', 'N', 'S'];
            } else if (this.overlapStrategy === 'radial') {
                priority = [radialAnchor, directionAnchor, quadrantAnchor, 'E', 'W', 'N', 'S'];
            } else {
                priority = [radialAnchor, quadrantAnchor, directionAnchor, ...rotatedKeys];
            }

            const unique = [];
            const seen = new Set();
            for (const key of [...priority, ...rotatedKeys]) {
                if (!anchors[key] || seen.has(key)) continue;
                seen.add(key);
                unique.push(anchors[key]);
            }
            return unique;
        },
        estimateLabelDimensions(lines) {
            const safeLines = lines.filter(Boolean);
            const maxChars = safeLines.reduce((max, line) => Math.max(max, line.length), 0);
            const width = Math.max(8, Math.round(maxChars * (this.labelSize * 0.56) + 6));
            const height = Math.max(8, Math.round(safeLines.length * (this.labelSize * 1.08) + 4));
            return { width, height };
        },
        getLabelDimensions(node, lines) {
            if (this.isPrivateLabel(node)) {
                return {
                    width: PRIVATE_LABEL_ICON_WIDTH + (PRIVATE_LABEL_ICON_PADDING * 2),
                    height: PRIVATE_LABEL_ICON_HEIGHT + (PRIVATE_LABEL_ICON_PADDING * 2),
                };
            }

            const cacheKey = `${this.labelMode}|${this.labelSize}|${lines.join('|')}`;
            if (!this.labelMeasureCache[cacheKey]) {
                this.labelMeasureCache[cacheKey] = this.estimateLabelDimensions(lines);
            }
            return this.labelMeasureCache[cacheKey];
        },
        getPrivateLabelIconX(anchor = 'start') {
            const width = PRIVATE_LABEL_ICON_WIDTH;
            if (anchor === 'end') return -width;
            if (anchor === 'middle') return -(width / 2);
            return 0;
        },
        buildLabelPlacement(node, anchorDef) {
            const x = node.x || 0;
            const y = node.y || 0;
            const dims = node._labelDims || { width: 10, height: 10 };
            const textX = x + anchorDef.dx;
            const textY = y + anchorDef.dy;

            let x0 = textX;
            if (anchorDef.anchor === 'end') x0 = textX - dims.width;
            if (anchorDef.anchor === 'middle') x0 = textX - (dims.width / 2);

            const y0 = textY - (dims.height / 2);
            return {
                key: anchorDef.key,
                anchor: anchorDef.anchor,
                textX,
                textY,
                leaderX: x + anchorDef.lx,
                leaderY: y + anchorDef.ly,
                x0,
                y0,
                x1: x0 + dims.width,
                y1: y0 + dims.height,
            };
        },
        rectsOverlap(a, b) {
            return !(a.x1 <= b.x0 || a.x0 >= b.x1 || a.y1 <= b.y0 || a.y0 >= b.y1);
        },
        scoreLabelPlacement(node, candidate, quadtree) {
            let score = 0;
            const margin = 3;
            if (candidate.x0 < margin) score += (margin - candidate.x0) * 300;
            if (candidate.y0 < margin) score += (margin - candidate.y0) * 300;
            if (candidate.x1 > this.simWidth - margin) score += (candidate.x1 - (this.simWidth - margin)) * 300;
            if (candidate.y1 > this.simHeight - margin) score += (candidate.y1 - (this.simHeight - margin)) * 300;

            // Prefer shorter leader lines and stable anchor choice to reduce visual noise.
            const leaderDx = candidate.leaderX - (node.x || 0);
            const leaderDy = candidate.leaderY - (node.y || 0);
            score += Math.hypot(leaderDx, leaderDy) * 0.7;
            if (node._labelPlacement?.key && node._labelPlacement.key !== candidate.key) {
                score += 45;
            }

            quadtree.visit((quad, x0, y0, x1, y1) => {
                if (!quad.data) {
                    return x1 < candidate.x0 || x0 > candidate.x1 || y1 < candidate.y0 || y0 > candidate.y1;
                }

                const placed = quad.data;
                if (!this.rectsOverlap(candidate, placed)) return false;

                const overlapX = Math.min(candidate.x1, placed.x1) - Math.max(candidate.x0, placed.x0);
                const overlapY = Math.min(candidate.y1, placed.y1) - Math.max(candidate.y0, placed.y0);
                const overlapArea = Math.max(0, overlapX) * Math.max(0, overlapY);
                score += 500 + (overlapArea * 4);
                return false;
            });

            return score;
        },
        getLabelPriorityScore(node) {
            const selectedId = this.labelPriorityContext.selectedId;
            const targetId = this.labelPriorityContext.targetId;
            const secondaryIds = this.labelPriorityContext.secondaryIds;
            const pathIds = this.labelPriorityContext.pathIds;
            const id = node.id;

            // Strongly prioritize context labels so they claim low-overlap space first.
            if (id === selectedId) return 1_000_000;
            if (id === targetId) return 900_000;
            if (pathIds.has(id)) return 800_000;
            if (secondaryIds.has(id)) return 700_000;

            // Then prioritize structurally important labels in dense centers.
            const degree = this.nodeDegreeMap.get(id) || 0;
            const cx = this.simWidth / 2;
            const cy = this.simHeight / 2;
            const dx = (node.x || 0) - cx;
            const dy = (node.y || 0) - cy;
            const dist = Math.hypot(dx, dy);
            const maxDist = Math.hypot(cx, cy) || 1;
            const centrality = 1 - Math.min(1, dist / maxDist);

            return (degree * 100) + (centrality * 1000);
        },
        getOrderedPlacementBatches(nodes = this.labelSelection?.data?.() || []) {
            const selectedId = this.selectedPerson;
            const baseSort = (a, b) => {
                if (a.id === selectedId) return -1;
                if (b.id === selectedId) return 1;
                return this.getLabelPriorityScore(b) - this.getLabelPriorityScore(a);
            };

            // For non-cluster modes, a single batch is enough.
            if (this.layoutMode !== 'cluster') {
                return [[...nodes].sort(baseSort)];
            }

            // In cluster mode, solve per connected component to reduce cross-island competition.
            const byComponent = new Map();
            for (const node of nodes) {
                const cid = node.componentId ?? 0;
                if (!byComponent.has(cid)) byComponent.set(cid, []);
                byComponent.get(cid).push(node);
            }

            const selectedNode = selectedId ? nodes.find(n => n.id === selectedId) : null;
            const selectedComponent = selectedNode?.componentId;

            const componentIds = [...byComponent.keys()].sort((a, b) => {
                if (selectedComponent !== undefined && selectedComponent !== null) {
                    if (a === selectedComponent) return -1;
                    if (b === selectedComponent) return 1;
                }
                const sizeA = this._componentSizes?.[a] || byComponent.get(a).length;
                const sizeB = this._componentSizes?.[b] || byComponent.get(b).length;
                return sizeB - sizeA;
            });

            return componentIds.map(cid => byComponent.get(cid).sort(baseSort));
        },
        shouldRecomputePlacement(alpha = 1, force = false) {
            if (force) return true;
            this.labelPlacementTick += 1;
            if (alpha > 0.12) return true;
            if (alpha > 0.07) return this.labelPlacementTick % 2 === 0;
            if (alpha > 0.04) return this.labelPlacementTick % 4 === 0;
            return this.labelPlacementTick % 8 === 0;
        },
        getForcedLabelIds() {
            const context = this.labelPriorityContext || {};
            const ids = new Set();
            if (context.selectedId) ids.add(context.selectedId);
            if (context.targetId) ids.add(context.targetId);
            context.pathIds?.forEach(id => ids.add(id));
            context.secondaryIds?.forEach(id => ids.add(id));
            this.nameFilterResult.matchedIds.forEach(id => ids.add(id));
            return ids;
        },
        getVisibleLabelNodes() {
            const transform = this._currentTransform || d3.zoomIdentity;
            return selectViewportLabels({
                nodes: this.simNodes.filter(node => this.isNodeRendered(node)),
                transform,
                width: this.simWidth,
                height: this.simHeight,
                forcedIds: this.getForcedLabelIds(),
                priorityForNode: node => this.getLabelPriorityScore(node),
            });
        },
        refreshLabels() {
            if (!this.layoutSettled || !this.graphRoot || !this.simNodes.length) return;

            const labelsGroup = this.graphRoot.select('.labels').empty()
                ? this.graphRoot.append('g').attr('class', 'labels')
                : this.graphRoot.select('.labels');
            const visibleNodes = this.getVisibleLabelNodes();
            this.labelSelection = labelsGroup
                .selectAll('g.label-item')
                .data(visibleNodes, d => d.id)
                .join(
                    enter => enter.append('g'),
                    update => update,
                    exit => exit.remove(),
                )
                .attr('class', d => `label-item ${this.isPrivateLabel(d) ? 'label-item--private' : 'label-item--name'}`)
                .attr('role', 'button')
                .attr('tabindex', 0)
                .attr('aria-label', d => this.getDisplayName(d))
                .on('click', (event, d) => {
                    event.stopPropagation();
                    this.selectPerson(d.id, true);
                })
                .on('mouseenter', (event, d) => {
                    this.previewPersonPath(d.id);
                })
                .on('mouseleave', (event, d) => {
                    this.clearPreviewPerson(d.id);
                });

            this.labelSelection.each((d, idx, arr) => {
                const renderKey = `${this.isPrivateLabel(d)}|${this.labelMode}|${this.labelSize}|${this.getDisplayName(d)}`;
                if (arr[idx].__labelRenderKey === renderKey) return;
                arr[idx].__labelRenderKey = renderKey;
                const label = d3.select(arr[idx]);
                const lines = this.formatLabelLines(d);
                d._labelLines = lines;
                d._labelPlacement = null;
                label.selectAll('*').remove();

                if (this.isPrivateLabel(d)) {
                    label.append('title').text(this.privateLabelTitle(d));
                    label.append('image')
                        .attr('class', 'label-lock')
                        .attr('href', lockIcon)
                        .attr('xlink:href', lockIcon)
                        .attr('width', PRIVATE_LABEL_ICON_WIDTH)
                        .attr('height', PRIVATE_LABEL_ICON_HEIGHT)
                        .attr('y', -(PRIVATE_LABEL_ICON_HEIGHT / 2));
                } else {
                    const text = label.append('text')
                        .attr('class', 'label-text')
                        .style('font-size', `${this.labelSize}px`);
                    lines.forEach((line, lineIdx) => {
                        if (!line) return;
                        text.append('tspan')
                            .attr('x', 0)
                            .attr('dy', lineIdx === 0 ? '0' : '1.05em')
                            .text(line);
                    });
                }

                d._labelDims = this.getLabelDimensions(d, lines);
            });

            this.updateLabelPositions(1, true);
            this.applyLabelStyles();
        },
        applyLabelStyles() {
            if (!this.labelSelection) return;
            const context = this.labelPriorityContext || {};
            const hasSelection = Boolean(context.selectedId);
            this.labelSelection
                .classed('label-dimmed', d => hasSelection
                    && d.id !== context.selectedId
                    && d.id !== context.targetId
                    && !context.pathIds?.has(d.id)
                    && !context.secondaryIds?.has(d.id));
            this.labelSelection.select('text').attr('fill', (d) => {
                if (d.id === context.targetId || context.pathIds?.has(d.id)) return '#0066CC';
                if (hasSelection && d.id !== context.selectedId && !context.secondaryIds?.has(d.id)) return '#AAA';
                return '#000';
            });
            this.labelSelection.select('image').attr('opacity', d => {
                if (!hasSelection) return 1;
                return d.id === context.selectedId || d.id === context.targetId
                    || context.pathIds?.has(d.id) || context.secondaryIds?.has(d.id) ? 1 : 0.35;
            });
        },
        updateLabelPositions(alpha = 1, force = false) {
            if (!this.labelSelection) return;

            if (this.shouldRecomputePlacement(alpha, force)) {
                const qt = d3.quadtree()
                    .x(d => (d.x0 + d.x1) / 2)
                    .y(d => (d.y0 + d.y1) / 2);

                const batches = this.getOrderedPlacementBatches();
                for (const batch of batches) {
                    for (const node of batch) {
                        const candidates = this.getAnchorCandidates(node);
                        let bestPlacement = null;
                        let bestScore = Infinity;

                        for (const candidate of candidates) {
                            const placement = this.buildLabelPlacement(node, candidate);
                            const score = this.scoreLabelPlacement(node, placement, qt);
                            if (score < bestScore) {
                                bestScore = score;
                                bestPlacement = placement;
                                if (score === 0) break;
                            }
                        }

                        node._labelPlacement = bestPlacement || this.buildLabelPlacement(node, this.getAnchorMap().E);
                        qt.add(node._labelPlacement);
                    }
                }
            }

            this.labelSelection
                .attr('transform', d => {
                    const p = d._labelPlacement || this.buildLabelPlacement(d, this.getAnchorMap().E);
                    return `translate(${p.textX}, ${p.textY})`;
                })
                .each((d, idx, arr) => {
                    const anchor = d._labelPlacement?.anchor || 'start';
                    const label = d3.select(arr[idx]);
                    label.select('text')
                        .attr('text-anchor', anchor)
                        .attr('dominant-baseline', 'middle');
                    label.select('image')
                        .attr('x', this.getPrivateLabelIconX(anchor));
                });


        },
        formatCount(n) {
            return new Intl.NumberFormat('nl-NL').format(n);
        },
        normalizeSurnameKey(value) {
            return normalizeClusterKey(value);
        },
        getCanonicalSurname(value, isMolukseBranchComponent = false) {
            return getCanonicalClusterSurname(value, { isMolukseBranchComponent });
        },
        buildClusterSurnameLinks() {
            const links = [];
            const seen = new Set();
            for (const link of this.rawLinks) {
                const sourceNode = this.nodeById.get(link.source?.id || link.source);
                const targetNode = this.nodeById.get(link.target?.id || link.target);
                const source = this.getCanonicalSurname(
                    sourceNode?.lastname,
                    sourceNode?.isMolukseBranch,
                ).key;
                const target = this.getCanonicalSurname(
                    targetNode?.lastname,
                    targetNode?.isMolukseBranch,
                ).key;
                if (!source || !target || source === target) continue;
                const edgeKey = [source, target].sort().join('\u0000');
                if (seen.has(edgeKey)) continue;
                seen.add(edgeKey);
                links.push({ source, target });
            }
            return links;
        },
        getClusterVisualVariant(surnameKey, variant = 'island', isMolukseBranchComponent = false) {
            if (variant === 'main') return variant;
            if (isMolukseBranchComponent) return MOLUKSE_VISUAL_VARIANT;
            if (variant === 'claasz' || variant === TATA_DNA_MATCH_VARIANT) return variant;
            if (variant === NEIRINCKX_BRANCH_VARIANT) return 'island';
            const molukseSurnameKeys = new Set(['taniwel', 'akollo', 'sapulete', 'ruspana']);
            return molukseSurnameKeys.has(surnameKey) ? MOLUKSE_VISUAL_VARIANT : variant;
        },
        getTopCountEntry(counts) {
            return [...counts.entries()].sort((a, b) => {
                if (b[1] !== a[1]) return b[1] - a[1];
                return a[0].localeCompare(b[0], 'nl', { sensitivity: 'base' });
            })[0] || null;
        },
        buildComponentMetadata() {
            const byComponent = new Map();
            for (const node of this.rawNodes) {
                const componentId = node.componentId ?? 0;
                if (!byComponent.has(componentId)) {
                    byComponent.set(componentId, {
                        componentId,
                        size: 0,
                        surnameCounts: new Map(),
                        labelCounts: new Map(),
                        molukseBranchCount: 0,
                        tataDnaMatchCount: 0,
                        neirinckxBranchCount: 0,
                    });
                }
                const bucket = byComponent.get(componentId);
                bucket.size += 1;
                if (node.isMolukseBranch) {
                    bucket.molukseBranchCount += 1;
                }
                if (node.isTataDnaMatch) {
                    bucket.tataDnaMatchCount += 1;
                }
                if (node.isNeirinckxBranch) {
                    bucket.neirinckxBranchCount += 1;
                }
                const surname = (node.lastname || '').trim();
                const label = (node.label || '').trim();
                if (surname) {
                    bucket.surnameCounts.set(surname, (bucket.surnameCounts.get(surname) || 0) + 1);
                }
                if (label) {
                    bucket.labelCounts.set(label, (bucket.labelCounts.get(label) || 0) + 1);
                }
            }

            const metadata = new Map();
            for (const bucket of byComponent.values()) {
                const surname = this.getTopCountEntry(bucket.surnameCounts)?.[0] || '';
                const fallbackLabel = this.getTopCountEntry(bucket.labelCounts)?.[0] || '';
                const isMolukseBranchComponent = bucket.componentId !== 0
                    && bucket.molukseBranchCount === bucket.size;
                const canonicalSurname = this.getCanonicalSurname(surname, isMolukseBranchComponent);
                const surnameKey = canonicalSurname.key;
                const surnameKeys = [...bucket.surnameCounts.keys()]
                    .map(value => this.getCanonicalSurname(value, isMolukseBranchComponent).key)
                    .filter(Boolean)
                    .filter((value, index, values) => values.indexOf(value) === index);
                const isTataDnaMatchComponent = bucket.componentId !== 0 && bucket.tataDnaMatchCount > 0;
                const isNeirinckxBranchComponent = bucket.componentId !== 0 && bucket.neirinckxBranchCount > 0;
                const variant = bucket.componentId === 0
                    ? 'main'
                    : (isMolukseBranchComponent
                        ? 'island'
                        : (isNeirinckxBranchComponent
                            ? NEIRINCKX_BRANCH_VARIANT
                            : (isTataDnaMatchComponent
                                ? TATA_DNA_MATCH_VARIANT
                                : (surnameKey === 'claasz' ? 'claasz' : 'island'))));

                metadata.set(bucket.componentId, {
                    componentId: bucket.componentId,
                    originalSurname: surname,
                    surname: canonicalSurname.label || surname,
                    surnameKey,
                    surnameKeys,
                    fallbackLabel,
                    size: bucket.size,
                    variant,
                    molukseBranchCount: bucket.molukseBranchCount,
                    isMolukseBranchComponent,
                    tataDnaMatchCount: bucket.tataDnaMatchCount,
                    neirinckxBranchCount: bucket.neirinckxBranchCount,
                    visualVariant: this.getClusterVisualVariant(surnameKey, variant, isMolukseBranchComponent),
                });
            }

            return metadata;
        },
        buildSurnameGroups(componentMetaById = this.componentMetaById) {
            return buildAutomaticSurnameGroups(componentMetaById.values());
        },
        buildNeirinckxBranchGroup(componentMetaById = this.componentMetaById) {
            const componentIds = [];
            let peopleCount = 0;
            for (const meta of componentMetaById.values()) {
                if (meta.componentId === 0
                    || meta.variant !== NEIRINCKX_BRANCH_VARIANT
                    || meta.isMolukseBranchComponent) {
                    continue;
                }
                componentIds.push(meta.componentId);
                peopleCount += meta.size;
            }

            if (componentIds.length === 0) {
                return null;
            }

            return {
                labelId: NEIRINCKX_BRANCH_VARIANT,
                componentId: NEIRINCKX_BRANCH_VARIANT,
                componentIds,
                label: 'Neirinckx',
                size: peopleCount,
                peopleCount,
                variant: 'neirinckx-group',
                visualVariant: 'island',
            };
        },
        buildIslandLabelData() {
            if (!this.componentMetaById?.size) {
                this.componentMetaById = this.buildComponentMetadata();
                this.surnameGroups = this.buildSurnameGroups(this.componentMetaById);
                this.neirinckxBranchGroup = this.buildNeirinckxBranchGroup(this.componentMetaById);
            }

            const groupedComponentIds = new Set();
            const neirinckxLabel = this.neirinckxBranchGroup?.componentIds?.length
                ? [this.neirinckxBranchGroup]
                : [];
            const branchGroupLabels = neirinckxLabel.map((group) => ({
                ...group,
                label: this.$t('personTree.clusterLabelWithCount', {
                    name: group.label,
                    count: this.formatCount(group.peopleCount),
                }),
            }));
            const groupLabels = [
                ...branchGroupLabels,
                ...(this.surnameGroups || []).map((group) => {
                group.componentIds.forEach(id => groupedComponentIds.add(id));
                return {
                    labelId: `surname:${group.groupKey || group.surnameKey}`,
                    componentId: `surname:${group.groupKey || group.surnameKey}`,
                    componentIds: [...group.componentIds],
                    label: this.$t('personTree.clusterLabelWithCount', {
                        name: group.surname,
                        count: this.formatCount(group.peopleCount),
                    }),
                    size: group.peopleCount,
                    surname: group.surname,
                    surnameKey: group.surnameKey,
                    variant: 'surname-group',
                    visualVariant: group.visualVariant,
                };
                })
            ];
            neirinckxLabel.forEach(group => group.componentIds.forEach(id => groupedComponentIds.add(id)));

            const componentLabels = [...this.componentMetaById.values()]
                .filter(meta => !groupedComponentIds.has(meta.componentId))
                .map((meta) => {
                    if (meta.variant === 'main') {
                        return {
                            ...meta,
                            labelId: `component:${meta.componentId}`,
                            componentIds: [meta.componentId],
                            label: this.$t('personTree.mainClusterLabel', { count: this.formatCount(meta.size) }),
                        };
                    }

                    const name = meta.surname || meta.fallbackLabel || this.$t('personTree.clusterFallback', { index: meta.componentId + 1 });
                    return {
                        ...meta,
                        labelId: `component:${meta.componentId}`,
                        componentIds: [meta.componentId],
                        label: this.$t('personTree.clusterLabelWithCount', {
                            name: meta.variant === 'claasz'
                                ? this.$t('personTree.claaszClusterLabel')
                                : name,
                            count: this.formatCount(meta.size),
                        }),
                    };
                });

            const subcladeLabels = this.getRenderableMainSubclades()
                .map(subclade => ({
                    labelId: `${MAIN_SUBCLADE_VARIANT}:${subclade.id}`,
                    componentId: 0,
                    componentIds: [0],
                    memberIds: subclade.memberIds,
                    zoomMemberIds: subclade.memberIds,
                    label: subclade.label,
                    size: subclade.memberIds.length,
                    angle: subclade.angle,
                    variant: MAIN_SUBCLADE_VARIANT,
                    visualVariant: MAIN_SUBCLADE_VARIANT,
                }));

            const targetAngles = new Map(this.getRenderableMainSubclades().map(item => [item.id, item.angle]));
            const researchRegions = this.buildLayoutRegions().filter(region => isUnlinkedResearchRegion(region, targetAngles));
            const researchZoneLabels = researchRegions.length ? [{
                labelId: 'research-zone', componentId: 'research-zone',
                componentIds: researchRegions.flatMap(region => region.componentIds),
                label: this.$t('personTree.otherResearchLabel'),
                size: researchRegions.reduce((sum, region) => sum + region.peopleCount, 0),
                variant: 'research-zone', visualVariant: 'island',
            }] : [];
            return [...componentLabels, ...groupLabels, ...subcladeLabels, ...researchZoneLabels]
                .sort((a, b) => b.size - a.size || String(a.labelId).localeCompare(String(b.labelId)));
        },
        refreshIslandLabels() {
            if (!this.graphRoot || !this.simNodes.length) return;

            const labelData = this.buildIslandLabelData();
            const labelGroup = this.graphRoot.select('.component-labels').empty()
                ? this.graphRoot.append('g').attr('class', 'component-labels')
                : this.graphRoot.select('.component-labels');

            this.islandLabelSelection = labelGroup
                .selectAll('g.component-label')
                .data(labelData, d => d.labelId || d.componentId)
                .join(
                    enter => {
                        const item = enter.append('g').attr('class', 'component-label');
                        item.append('rect').attr('class', 'component-label__proximity');
                        item.append('rect').attr('class', 'component-label__bg');
                        item.append('text').attr('class', 'component-label__text').attr('text-anchor', 'middle');
                        return item;
                    },
                    update => update,
                    exit => exit.remove(),
                );

            this.islandLabelSelection
                .attr('class', d => {
                    const variant = d.variant || 'island';
                    const visualVariant = d.visualVariant || variant;
                    return `component-label component-label--${variant} component-label--${visualVariant}`;
                })
                .on('click', (event, d) => {
                    event.stopPropagation();
                    if (d.variant === MAIN_SUBCLADE_VARIANT) {
                        this.zoomToNodeIds(d.zoomMemberIds || d.memberIds || []);
                    } else if (['surname-group', 'research-zone'].includes(d.variant)) {
                        this.zoomToComponents(d.componentIds || []);
                    } else {
                        this.zoomToCluster(d.componentId);
                    }
                })
                .on('mouseenter', (event, d) => {
                    d._hoverDx = 0;
                    d._hoverDy = 0;
                    d3.select(event.currentTarget).classed('component-label--hovering', true);
                    this.applyIslandLabelTransforms();
                })
                .on('mousemove', (event, d) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    const cx = rect.left + (rect.width / 2);
                    const cy = rect.top + (rect.height / 2);
                    const rawDx = event.clientX - cx;
                    const rawDy = event.clientY - cy;
                    const dist = Math.hypot(rawDx, rawDy);
                    const maxRadius = 110;
                    const maxShift = 20;
                    if (!dist) {
                        d._hoverDx = 0;
                        d._hoverDy = 0;
                    } else {
                        const clamped = Math.min(dist, maxRadius);
                        const shift = (clamped / maxRadius) * maxShift;
                        d._hoverDx = (rawDx / dist) * shift;
                        d._hoverDy = (rawDy / dist) * shift;
                    }
                    this.applyIslandLabelTransforms();
                })
                .on('mouseleave', (event, d) => {
                    d._hoverDx = 0;
                    d._hoverDy = 0;
                    d3.select(event.currentTarget).classed('component-label--hovering', false);
                    this.applyIslandLabelTransforms();
                });

            this.islandLabelSelection.select('.component-label__text')
                .text(d => d.label)
                .attr('dominant-baseline', 'middle');

            this.updateIslandLabelPositions(true);
        },
        getIslandLabelKey(d) {
            return d?.labelId || d?.componentId;
        },
        computeLabelCenterFromNodes(labelData) {
            const memberIds = Array.isArray(labelData.memberIds)
                ? new Set(labelData.memberIds)
                : null;
            const componentIds = new Set(labelData.componentIds || [labelData.componentId]);
            const nodes = (this.simNodes || []).filter(node => {
                if (memberIds) return memberIds.has(node.id)
                    && Number.isFinite(node.x) && Number.isFinite(node.y);
                if (!componentIds.has(node.componentId)) return false;
                return Number.isFinite(node.x) && Number.isFinite(node.y);
            });
            if (!nodes.length) return null;

            const xs = nodes.map(node => node.x);
            const ys = nodes.map(node => node.y);
            return {
                x: (Math.min(...xs) + Math.max(...xs)) / 2,
                y: (Math.min(...ys) + Math.max(...ys)) / 2,
            };
        },
        computeLabelCenterFromCentroids(labelData, centroids) {
            const componentIds = labelData.componentIds || [labelData.componentId];
            const points = componentIds
                .map(componentId => centroids.get(componentId))
                .filter(Boolean);
            if (!points.length) return null;

            return {
                x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
                y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
            };
        },
        computeLiveIslandLabelCenters(labelData) {
            const componentBounds = this.computeComponentBounds();
            const centers = new Map();
            for (const item of labelData || []) {
                if (Array.isArray(item.memberIds)) {
                    const center = this.computeLabelCenterFromNodes(item);
                    if (center) centers.set(this.getIslandLabelKey(item), center);
                    continue;
                }
                const ids = item.componentIds || [item.componentId];
                const bounds = ids.map(id => componentBounds.get(id)).filter(Boolean);
                if (!bounds.length) continue;
                centers.set(this.getIslandLabelKey(item), {
                    x: (Math.min(...bounds.map(bound => bound.minX)) + Math.max(...bounds.map(bound => bound.maxX))) / 2,
                    y: item.variant === 'research-zone' ? Math.min(...bounds.map(bound => bound.minY)) - 140
                        : (Math.min(...bounds.map(bound => bound.minY)) + Math.max(...bounds.map(bound => bound.maxY))) / 2,
                });
            }
            return centers;
        },
        computeFallbackIslandLabelCenters(labelData) {
            const centroids = this.layoutCentroids || this.computeLiveComponentCenters();
            const centers = new Map();
            for (const item of labelData || []) {
                if (Array.isArray(item.memberIds)) {
                    const center = this.computeLabelCenterFromNodes(item);
                    if (center) centers.set(this.getIslandLabelKey(item), center);
                    continue;
                }
                const center = this.computeLabelCenterFromCentroids(item, centroids);
                if (center) centers.set(this.getIslandLabelKey(item), center);
            }
            return centers;
        },
        resolveComponentLabelCenters(labelData, baseCenters, zoomK) {
            const resolved = new Map(baseCenters);
            const priority = item => item.variant === 'main' ? 0 : item.variant === MAIN_SUBCLADE_VARIANT ? 1
                : item.variant === 'research-zone' ? 2 : 3;
            const ordered = (labelData || []).filter(item => baseCenters.has(this.getIslandLabelKey(item)))
                .sort((a, b) => priority(a) - priority(b) || b.size - a.size
                    || String(a.labelId).localeCompare(String(b.labelId)));
            const svgBounds = this.zoomSelection?.node()?.getBoundingClientRect();
            const placed = [...document.querySelectorAll('.tree-legend, .stamboom-view-toggles, #zoom-buttons, #fullscreen-button, #toggle-sidebar-button')]
                .map(element => element.getBoundingClientRect())
                .filter(bounds => bounds.width && bounds.height)
                .map(bounds => ({ x: bounds.x + bounds.width / 2 - (svgBounds?.x || 0),
                    y: bounds.y + bounds.height / 2 - (svgBounds?.y || 0), width: bounds.width, height: bounds.height }));
            const safeZoom = Math.max(zoomK, 0.001);
            const transform = this.zoomSelection?.node()?.__zoom || { x: 0, y: 0 };
            const centerX = this.simWidth / 2;
            const centerY = this.simHeight / 2;
            for (const item of ordered) {
                const key = this.getIslandLabelKey(item);
                const base = baseCenters.get(key);
                const baseX = base.x * safeZoom + transform.x;
                const baseY = base.y * safeZoom + transform.y - (item.variant === 'research-zone' ? 32 : 0);
                const width = Math.max(68, String(item.label || '').length * 7.5 + 28);
                const height = 32;
                const direction = Number.isFinite(item.angle) ? item.angle : Math.atan2(baseY - centerY, baseX - centerX);
                const baseVisible = baseX >= 0 && baseX <= this.simWidth && baseY >= 0 && baseY <= this.simHeight;
                const available = (x, y) => (!baseVisible || (x >= width / 2 + 8 && x <= this.simWidth - width / 2 - 8
                    && y >= height / 2 + 8 && y <= this.simHeight - height / 2 - 8))
                    && !placed.some(label => Math.abs(x - label.x) < (width + label.width) / 2 + 8
                        && Math.abs(y - label.y) < (height + label.height) / 2 + 6);
                let screenX = baseX;
                let screenY = baseY;
                let found = available(screenX, screenY);
                for (let ring = 1; ring <= 64 && !found; ring += 1) {
                    for (let step = 0; step < 16; step += 1) {
                        const angle = direction + step * Math.PI / 8;
                        const x = baseX + Math.cos(angle) * ring * 12;
                        const y = baseY + Math.sin(angle) * ring * 12;
                        if (!available(x, y)) continue;
                        screenX = x; screenY = y; found = true;
                        break;
                    }
                }
                // Dense overview labels reappear when zooming into their group.
                if (found) placed.push({ x: screenX, y: screenY, width, height });
                resolved.set(key, { x: (screenX - transform.x) / safeZoom, y: (screenY - transform.y) / safeZoom, hidden: !found });
            }
            return resolved;
        },
        applyIslandLabelTransforms() {
            if (!this.islandLabelSelection) return;

            const liveCenters = this.islandLabelCenters || new Map();
            const fallbackCentroids = this.islandLabelFallbackCentroids || new Map();
            const zoomK = Math.max(this.currentZoomK || 1, 0.001);
            const inverseScale = 1 / zoomK;
            const baseCenters = new Map(fallbackCentroids);
            for (const [key, center] of liveCenters) baseCenters.set(key, center);
            const displayCenters = this.resolveComponentLabelCenters(
                this.islandLabelSelection.data(),
                baseCenters,
                zoomK,
            );

            this.islandLabelSelection
                .style('visibility', d => displayCenters.get(this.getIslandLabelKey(d))?.hidden ? 'hidden' : null)
                .attr('transform', d => {
                    const key = this.getIslandLabelKey(d);
                    const centroid = displayCenters.get(key) || { x: 0, y: 0 };
                    const hoverDx = (d._hoverDx || 0) / zoomK;
                    const hoverDy = (d._hoverDy || 0) / zoomK;
                    return `translate(${centroid.x + hoverDx}, ${centroid.y + hoverDy}) scale(${inverseScale})`;
                });
        },
        updateIslandLabelPositions(recomputeCenters = true) {
            if (!this.islandLabelSelection || !this.simNodes.length) return;
            const labelData = this.islandLabelSelection.data();

            if (recomputeCenters || !this.islandLabelCenters) {
                this.islandLabelCenters = this.computeLiveIslandLabelCenters(labelData);
            }
            if (recomputeCenters || !this.islandLabelFallbackCentroids) {
                this.islandLabelFallbackCentroids = this.computeFallbackIslandLabelCenters(labelData);
            }

            this.applyIslandLabelTransforms();

            this.islandLabelSelection
                .each(function (d) {
                    const group = d3.select(this);
                    const text = group.select('.component-label__text');
                    const proximity = group.select('.component-label__proximity');
                    const rect = group.select('.component-label__bg');
                    const groupNode = group.node();
                    if (groupNode.__componentLabelKey !== d.label) {
                        groupNode.__componentLabelKey = d.label;
                        groupNode.__componentLabelBBox = text.node()?.getBBox() || null;
                    }
                    const bbox = groupNode.__componentLabelBBox;
                    if (!bbox) return;
                    const padX = 12;
                    const padY = 6;
                    const proximityPadX = 30;
                    const proximityPadY = 18;
                    proximity
                        .attr('x', bbox.x - proximityPadX)
                        .attr('y', bbox.y - proximityPadY)
                        .attr('width', bbox.width + (proximityPadX * 2))
                        .attr('height', bbox.height + (proximityPadY * 2));
                    rect
                        .attr('x', bbox.x - padX)
                        .attr('y', bbox.y - padY)
                        .attr('width', bbox.width + (padX * 2))
                        .attr('height', bbox.height + (padY * 2))
                        .attr('rx', 3)
                        .attr('ry', 3);
                });
        },
        getSurnameGroupBoundaryData() {
            if (!this.simNodes.length) return [];

            const componentBounds = this.computeComponentBounds();
            const groupedComponentIds = new Set();
            const groupedBoundaries = [
                ...(this.neirinckxBranchGroup?.componentIds?.length ? [this.neirinckxBranchGroup] : []),
                ...(this.surnameGroups || []),
            ];
            groupedBoundaries.forEach(group => (
                group.componentIds.forEach(componentId => groupedComponentIds.add(componentId))
            ));
            const singletonBoundaries = [...this.componentMetaById.values()]
                .filter(meta => meta.componentId !== 0 && !groupedComponentIds.has(meta.componentId))
                .map(meta => ({
                    labelId: `component:${meta.componentId}`,
                    label: meta.variant === 'claasz'
                        ? this.$t('personTree.claaszClusterLabel')
                        : (meta.surname || meta.fallbackLabel),
                    componentIds: [meta.componentId],
                    variant: meta.variant,
                    visualVariant: meta.visualVariant,
                }));

            const boundaryGroups = [
                ...this.getRenderableMainSubclades().map(subclade => ({
                    labelId: `${MAIN_SUBCLADE_VARIANT}:${subclade.id}`,
                    label: subclade.label,
                    memberIds: subclade.memberIds,
                    componentIds: [0],
                    variant: MAIN_SUBCLADE_VARIANT,
                    visualVariant: MAIN_SUBCLADE_VARIANT,
                })),
                ...groupedBoundaries,
                ...singletonBoundaries,
            ];

            return boundaryGroups
                .map((group) => {
                    const points = [];
                    if (Array.isArray(group.memberIds)) {
                        const memberIds = new Set(group.memberIds);
                        for (const node of this.simNodes) {
                            if (!memberIds.has(node.id)
                                || !Number.isFinite(node.x) || !Number.isFinite(node.y)) continue;
                            const pad = 24;
                            points.push(
                                [node.x - pad, node.y - pad],
                                [node.x + pad, node.y - pad],
                                [node.x + pad, node.y + pad],
                                [node.x - pad, node.y + pad],
                            );
                        }
                    } else {
                        for (const componentId of group.componentIds) {
                            const bounds = componentBounds.get(componentId);
                            if (!bounds) continue;
                            const pad = Math.max(32, Math.min(78, Math.sqrt(bounds.count) * 8));
                            points.push(
                                [bounds.minX - pad, bounds.minY - pad],
                                [bounds.maxX + pad, bounds.minY - pad],
                                [bounds.maxX + pad, bounds.maxY + pad],
                                [bounds.minX - pad, bounds.maxY + pad],
                            );
                        }
                    }

                    const hull = points.length >= 3 ? d3.polygonHull(points) : null;
                    if (!hull) return null;

                    const path = d3.line()
                        .curve(d3.curveBasisClosed)(hull);

                    return {
                        boundaryId: group.labelId || `surname:${group.groupKey || group.surnameKey}`,
                        label: group.label || group.surname,
                        componentIds: group.componentIds,
                        memberIds: group.memberIds,
                        variant: group.variant,
                        visualVariant: group.visualVariant,
                        path,
                    };
                })
                .filter(Boolean);
        },
        refreshSurnameGroupBoundaries() {
            if (!this.graphRoot) return;

            const boundaryGroup = this.graphRoot.select('.surname-group-boundaries').empty()
                ? this.graphRoot.insert('g', ':first-child').attr('class', 'surname-group-boundaries')
                : this.graphRoot.select('.surname-group-boundaries');

            this.surnameGroupBoundarySelection = boundaryGroup
                .selectAll('path.surname-group-boundary')
                .data(this.getSurnameGroupBoundaryData(), d => d.boundaryId)
                .join(
                    enter => enter.append('path')
                        .attr('aria-label', d => `${d.label} clusters`),
                    update => update,
                    exit => exit.remove(),
                )
                .attr('class', d => {
                    const visualVariant = d.visualVariant || 'default';
                    return `surname-group-boundary surname-group-boundary--${visualVariant}`;
                })
                .attr('d', d => d.path);
        },
        computeAffinityNodeBounds({ componentIds = [], memberIds = [] } = {}) {
            const componentIdSet = new Set(componentIds);
            const memberIdSet = new Set(memberIds);
            const nodes = this.simNodes.filter(node => (
                (componentIdSet.size && componentIdSet.has(node.componentId))
                || (memberIdSet.size && memberIdSet.has(node.id))
            )).filter(node => Number.isFinite(node.x) && Number.isFinite(node.y));
            if (!nodes.length) return null;

            const minX = Math.min(...nodes.map(node => node.x));
            const maxX = Math.max(...nodes.map(node => node.x));
            const minY = Math.min(...nodes.map(node => node.y));
            const maxY = Math.max(...nodes.map(node => node.y));
            const x = (minX + maxX) / 2;
            const y = (minY + maxY) / 2;
            const radius = Math.max(34, ...nodes.map(node => Math.hypot(node.x - x, node.y - y) + 18));
            return { x, y, radius };
        },
        buildAffinityConnectorPath(source, target, seed = '') {
            let dx = source.x - target.x;
            let dy = source.y - target.y;
            const distance = Math.hypot(dx, dy);
            if (!Number.isFinite(distance) || distance < 1) return '';
            dx /= distance;
            dy /= distance;

            const startX = target.x + dx * Math.min(target.radius, distance * 0.35);
            const startY = target.y + dy * Math.min(target.radius, distance * 0.35);
            const endX = source.x - dx * Math.min(source.radius, distance * 0.35);
            const endY = source.y - dy * Math.min(source.radius, distance * 0.35);
            const curveDirection = String(seed).split('')
                .reduce((sum, character) => sum + character.charCodeAt(0), 0) % 2 ? 1 : -1;
            const curve = Math.min(64, Math.max(18, distance * 0.08)) * curveDirection;
            const controlX = (startX + endX) / 2 - dy * curve;
            const controlY = (startY + endY) / 2 + dx * curve;
            return `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
        },
        getLayoutAffinityConnectorData() {
            const subclades = new Map(this.getRenderableMainSubclades()
                .map(subclade => [subclade.id, subclade]));
            const regions = this.buildLayoutRegions(1);
            const regionsById = new Map(regions.map(region => [region.id, region]));

            return regions
                .filter(region => region.affinityConnector)
                .map((region) => {
                    const source = this.computeAffinityNodeBounds({ componentIds: region.componentIds });
                    const connectorTargetType = region.connectorTargetType || 'subclade';
                    const connectorTargetId = region.connectorTargetId || region.affinityTarget;
                    const targetRegion = connectorTargetType === 'region'
                        ? regionsById.get(connectorTargetId)
                        : null;
                    const targetSubclade = connectorTargetType === 'subclade'
                        ? subclades.get(connectorTargetId)
                        : null;
                    const target = targetRegion
                        ? this.computeAffinityNodeBounds({ componentIds: targetRegion.componentIds })
                        : this.computeAffinityNodeBounds({ memberIds: targetSubclade?.memberIds || [] });
                    if (!source || !target) return null;
                    const path = this.buildAffinityConnectorPath(source, target, region.id);
                    if (!path) return null;
                    return {
                        ...region,
                        connectorTargetType,
                        connectorTargetId,
                        connectorId: `${region.id}->${connectorTargetType}:${connectorTargetId}`,
                        componentId: region.componentIds[0],
                        path,
                    };
                })
                .filter(Boolean);
        },
        refreshLayoutAffinityConnectors() {
            if (!this.graphRoot) return;

            const connectorGroup = this.graphRoot.select('.layout-affinity-connectors').empty()
                ? this.graphRoot.insert('g', '.links').attr('class', 'layout-affinity-connectors')
                : this.graphRoot.select('.layout-affinity-connectors');

            this.affinityConnectorSelection = connectorGroup
                .selectAll('path.layout-affinity-connector')
                .data(this.getLayoutAffinityConnectorData(), d => d.connectorId)
                .join(
                    enter => enter.append('path').attr('aria-hidden', 'true'),
                    update => update,
                    exit => exit.remove(),
                )
                .attr('class', d => (
                    `layout-affinity-connector layout-affinity-connector--${d.affinityKind}`
                ))
                .attr('d', d => d.path);

            this.applyTreeFilterVisibility();
        },
        getHighlightVisibleLabelIds() {
            const context = this.labelPriorityContext;
            if (!context?.selectedId) return null;

            const ids = new Set([context.selectedId]);
            if (context.targetId) ids.add(context.targetId);
            context.pathIds?.forEach(id => ids.add(id));
            context.secondaryIds?.forEach(id => ids.add(id));
            return ids;
        },
        applyLabelVisibility() {
            this.applyLabelStyles();
        },
        getHighlightRouteLinkData(pathResults = []) {
            const links = this.linkSelection?.data() || [];
            const linkByEdge = new Map();
            links.forEach((link) => {
                const sourceId = link.source?.id || link.source;
                const targetId = link.target?.id || link.target;
                linkByEdge.set(`${sourceId}-${targetId}`, link);
                linkByEdge.set(`${targetId}-${sourceId}`, link);
            });

            const routeLinks = [];
            pathResults.forEach((result, routeIndex) => {
                const ids = result.path.map(node => node.id);
                for (let edgeIndex = 0; edgeIndex < ids.length - 1; edgeIndex += 1) {
                    const sourceId = ids[edgeIndex];
                    const targetId = ids[edgeIndex + 1];
                    const link = linkByEdge.get(`${sourceId}-${targetId}`);
                    if (!link) continue;
                    const edgeKey = [sourceId, targetId].sort().join('-');
                    routeLinks.push({ link, routeIndex, edgeIndex, edgeKey });
                }
            });

            const byEdge = new Map();
            routeLinks.forEach((entry) => {
                if (!byEdge.has(entry.edgeKey)) byEdge.set(entry.edgeKey, []);
                byEdge.get(entry.edgeKey).push(entry);
            });
            byEdge.forEach((entries) => entries.forEach((entry, index) => {
                entry.sharedIndex = index;
                entry.sharedCount = entries.length;
            }));
            return routeLinks;
        },
        getRouteLineCoordinates(entry) {
            const source = entry.link.source;
            const target = entry.link.target;
            const dx = (target.x ?? 0) - (source.x ?? 0);
            const dy = (target.y ?? 0) - (source.y ?? 0);
            const length = Math.hypot(dx, dy) || 1;
            const offset = ((entry.sharedIndex || 0) - ((entry.sharedCount || 1) - 1) / 2) * 3.5;
            const offsetX = (-dy / length) * offset;
            const offsetY = (dx / length) * offset;
            return {
                x1: (source.x ?? 0) + offsetX,
                y1: (source.y ?? 0) + offsetY,
                x2: (target.x ?? 0) + offsetX,
                y2: (target.y ?? 0) + offsetY,
            };
        },
        refreshHighlightOverlay(pathResults = [], pathSet = new Set(), activePerson = '', targetReached = null) {
            if (!this.highlightOverlaySelection) return;

            const overlayLinks = this.getHighlightRouteLinkData(pathResults);
            const overlayNodeIds = new Set(pathSet);
            if (activePerson) overlayNodeIds.add(activePerson);
            if (targetReached) overlayNodeIds.add(targetReached);
            const overlayNodes = (this.simNodes || [])
                .filter(node => overlayNodeIds.has(node.id))
                .map((node) => ({
                    node,
                    routeIndex: Math.max(0, pathResults.findIndex(result => result.path.some(item => item.id === node.id))),
                }));

            this.highlightOverlaySelection
                .selectAll('line.highlight-overlay__link')
                .data(overlayLinks, d => {
                    const sourceId = d.link.source?.id || d.link.source;
                    const targetId = d.link.target?.id || d.link.target;
                    return `${d.routeIndex}-${sourceId}-${targetId}`;
                })
                .join(
                    enter => enter.append('line').attr('class', 'highlight-overlay__link'),
                    update => update,
                    exit => exit.remove(),
                )
                .style('stroke', d => this.getRouteColor(d.routeIndex), 'important')
                .attr('x1', d => this.getRouteLineCoordinates(d).x1)
                .attr('y1', d => this.getRouteLineCoordinates(d).y1)
                .attr('x2', d => this.getRouteLineCoordinates(d).x2)
                .attr('y2', d => this.getRouteLineCoordinates(d).y2);

            this.highlightOverlaySelection
                .selectAll('circle.highlight-overlay__node')
                .data(overlayNodes, d => d.node.id)
                .join(
                    enter => enter.append('circle')
                        .attr('class', 'highlight-overlay__node')
                        .attr('r', 8)
                        .on('click', (event, d) => {
                            event.stopPropagation();
                            this.selectPerson(d.node.id, true);
                        }),
                    update => update,
                    exit => exit.remove(),
                )
                .classed('highlight-overlay__node--selected', d => d.node.id === activePerson)
                .classed('highlight-overlay__node--target', d => d.node.id === targetReached)
                .classed('highlight-overlay__node--path', d => pathSet.has(d.node.id)
                    && d.node.id !== activePerson && d.node.id !== targetReached)
                .style('stroke', d => d.node.id === activePerson ? '#6f1016'
                    : d.node.id === targetReached ? '#003d7a' : this.getRouteColor(d.routeIndex))
                .attr('cx', d => (d.node.x ?? 0))
                .attr('cy', d => (d.node.y ?? 0));
        },
        clearHighlightOverlay() {
            if (!this.highlightOverlaySelection) return;
            this.highlightOverlaySelection.selectAll('*').remove();
        },
        updateHighlightOverlayPositions() {
            if (!this.highlightOverlaySelection) return;

            this.highlightOverlaySelection
                .selectAll('line.highlight-overlay__link')
                .attr('x1', d => this.getRouteLineCoordinates(d).x1)
                .attr('y1', d => this.getRouteLineCoordinates(d).y1)
                .attr('x2', d => this.getRouteLineCoordinates(d).x2)
                .attr('y2', d => this.getRouteLineCoordinates(d).y2);

            this.highlightOverlaySelection
                .selectAll('circle.highlight-overlay__node')
                .attr('cx', d => (d.node.x ?? 0))
                .attr('cy', d => (d.node.y ?? 0));
        },
        _applyViewportCulling() {
            if (!this.nodeSelection || !this.linkSelection) return;
            if (this.hasNameFilters || this.hiddenLegendCategories.length) {
                this.applyTreeFilterVisibility();
                return;
            }
            const t = this._currentTransform;
            if (!t) return;
            const margin = 300; // world-space pixel buffer to avoid pop-in at edges
            const left = (-t.x / t.k) - margin;
            const top = (-t.y / t.k) - margin;
            const right = ((this.simWidth - t.x) / t.k) + margin;
            const bottom = ((this.simHeight - t.y) / t.k) + margin;
            const inBounds = (x, y) => x >= left && x <= right && y >= top && y <= bottom;
            this.nodeSelection.attr('display', d => this.isNodeRendered(d)
                && inBounds(d.x ?? 0, d.y ?? 0) ? null : 'none');
            this.linkSelection.attr('display', d => {
                if (!this.isLinkRendered(d)) return 'none';
                const sx = d.source.x ?? 0, sy = d.source.y ?? 0;
                const ex = d.target.x ?? 0, ey = d.target.y ?? 0;
                return (inBounds(sx, sy) || inBounds(ex, ey)) ? null : 'none';
            });
        },
        findPathsToJeffrey(fromPersonId) {
            const targetId = this.jeffreyTargetId;
            if (!fromPersonId || !targetId) {
                return { routes: [] };
            }

            const result = findLogicalRelationshipPath({
                graph: this.relationshipGraph,
                fromId: fromPersonId,
                targetId,
            });
            return {
                routes: result.path.length ? [{
                    path: result.path.map(id => this.getPersonById(id)),
                    targetReached: targetId,
                }] : [],
            };
        },
        getRouteColor() {
            return ROUTE_COLOR;
        },
        selectPerson(personId, scrollToTop = false) {
            if (this.selectedPerson === personId) {
                this.selectedPerson = '';
                this.previewPerson = '';
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.pathRoutes = [];
                this.selectedPersonMeta = null;
                this.highlightPersonInGraph();
                return;
            }

            this.previewPerson = '';
            this.selectedPerson = personId;
            this.selectedPersonMeta = this.getPersonById(personId);
            this.computeSidebarDetails();
            this.highlightPersonInGraph();
            this.centerOnNode(personId);

            if (scrollToTop) {
                this.$nextTick(() => this.$refs.personList?.scrollToKey(personId, 'center'));
            }
        },
        computeSidebarDetails() {
            if (!this.selectedPerson) {
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.pathRoutes = [];
                return;
            }

            const adjacency = this.buildAdjacency();
            const neighborIds = new Set(adjacency.get(this.selectedPerson) || []);
            const pathSearch = this.findPathsToJeffrey(this.selectedPerson);
            const pathResults = pathSearch.routes;

            this.secondaryNodes = [...neighborIds]
                .map(id => this.getPersonById(id))
                .filter(Boolean)
                .sort((a, b) => a.label.localeCompare(b.label, 'nl', { sensitivity: 'base' }));

            this.pathRoutes = pathResults
                .map(result => result.path.filter(person => person.id !== this.selectedPerson))
                .filter(route => route.length);
            const pathNodeIds = new Set();
            this.pathNodes = this.pathRoutes.flat().filter((person) => {
                if (pathNodeIds.has(person.id)) return false;
                pathNodeIds.add(person.id);
                return true;
            });
        },
        renderFullGraph(restoreState = null) {
            const container = d3.select('#person-tree-container').node();
            if (!container || !this.rawNodes.length) return;

            this.teardownGraph();
            this.graphLoading = true;
            this.graphProgress = 10;
            this.graphSimulationDone = false;
            this.graphFitDone = false;
            this.graphTickCount = 0;

            const width = container.offsetWidth;
            const height = container.offsetHeight;

            // Deep-copy nodes so D3 simulation can mutate x/y without affecting rawNodes
            const currentLayout = isCurrentPersonLayout(restoreState?.layout);
            this.manualZoomDetected = currentLayout && restoreState?.view?.manualZoomDetected === true;
            const restoredPositions = new Map((currentLayout ? restoreState?.layout?.positions || [] : [])
                .map(position => [position.id, position]));
            const nodes = this.rawNodes.map((node) => {
                const position = restoredPositions.get(node.id);
                return {
                    ...node,
                    ...(position && Number.isFinite(position.x) && Number.isFinite(position.y)
                        ? { x: position.x, y: position.y }
                        : {}),
                };
            });
            const links = this.rawLinks.map(l => ({ ...l }));
            this.simNodes = markRaw(nodes);
            this.simWidth = width;
            this.simHeight = height;

            this.zoomBehavior = d3.zoom()
                .on('zoom', (event) => {
                    // Cheap state updates — always synchronous
                    this.currentZoomK = event.transform?.k || 1;
                    this._currentTransform = event.transform;
                    if (event.sourceEvent && !this.applyingViewportFit) {
                        this.manualZoomDetected = true;
                    }
                    // Expensive DOM work gated to one update per animation frame
                    this._pendingZoomTransform = event.transform;
                    if (!this._zoomRaf) {
                        this._zoomRaf = requestAnimationFrame(() => {
                            this._zoomRaf = null;
                            if (this._pendingZoomTransform) {
                                this.graphRoot?.attr('transform', this._pendingZoomTransform);
                                this._pendingZoomTransform = null;
                            }
                            if (this.islandLabelSelection) {
                                this.applyIslandLabelTransforms();
                            }
                        });
                    }
                })
                .on('start', (event) => {
                    // Only treat real pointer/wheel events as interactions
                    // (programmatic transitions from fitGraphToViewport should not suppress labels)
                    if (event.sourceEvent) this._isInteracting = true;
                })
                .on('end', () => {
                    this._isInteracting = false;
                    if (this.layoutSettled) this.refreshLabels();
                    if (this.islandLabelSelection) this.applyIslandLabelTransforms();
                    this._applyViewportCulling();
                });

            const svgSelection = d3.select('#person-tree-container')
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .call(this.zoomBehavior);
            this.zoomSelection = svgSelection;

            svgSelection.on('click', () => {
                this.selectedPerson = '';
                this.previewPerson = '';
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.pathRoutes = [];
                this.selectedPersonMeta = null;
                this.highlightPersonInGraph();
            });

            const root = svgSelection.append('g');
            this.graphRoot = root;

            const link = root.append('g')
                .attr('class', 'links')
                .selectAll('line')
                .data(links)
                .enter()
                .append('line')
                .classed(MAIN_SUBCLADE_VARIANT, (d) => {
                    const sourceSubclade = this.getMainSubclade(d.source);
                    return Boolean(sourceSubclade && sourceSubclade === this.getMainSubclade(d.target));
                })
                .attr('stroke', '#aaa')
                .attr('stroke-width', 1);

            const node = root.append('g')
                .attr('class', 'nodes')
                .selectAll('circle')
                .data(nodes)
                .enter()
                .append('circle')
                .attr('data-id', d => d.id)
                .attr('data-category', d => this.getNodeLegendCategory(d))
                .classed(MAIN_SUBCLADE_VARIANT, d => this.isMainSubcladeNode(d))
                .classed('subclade-gateway', d => this.mainSubcladeAnchorIds.has(d.id))
                .classed('tata-dna-match', d => this.getNodeLegendCategory(d) === TATA_DNA_MATCH_VARIANT)
                .classed('molukse', d => this.getNodeLegendCategory(d) === MOLUKSE_VISUAL_VARIANT)
                .classed('neirinckx-branch', d => d.isNeirinckxBranch)
                .attr('r', 5)
                .attr('stroke', 'white')
                .attr('stroke-width', 1)
                .on('click', (event, d) => {
                    event.stopPropagation();
                    this.selectPerson(d.id, true);
                });

            this.nodeSelection = node;
            this.linkSelection = link;
            this.highlightOverlaySelection = root.append('g').attr('class', 'highlight-overlay');
            root.append('g').attr('class', 'labels');
            this.applyNameFilterVisibility();
            this.viewportFitApplied = false;

            // Create simulation stopped — forces will be applied by applyLayout
            this.simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id(d => d.id).distance(60).strength(0.5))
                .force('charge', d3.forceManyBody().strength(-60))
                .force('collide', d3.forceCollide(6))
                .alphaDecay(0.04)
                .alphaMin(0.02)
                .stop();

            this.simulation
                .on('tick', () => {
                    const alpha = this.simulation.alpha();
                    this.graphTickCount += 1;
                    const simProgress = Math.max(0, Math.min(90, ((0.8 - alpha) / 0.78) * 90));
                    const nextProgress = Math.floor(simProgress);
                    if (nextProgress >= this.graphProgress + 2) this.graphProgress = nextProgress;
                    this.schedulePositionPaint();
                })
                .on('end', () => this.finalizeLayout());

            const canRestoreSettled = currentLayout && Boolean(restoreState?.layout?.settled)
                && restoredPositions.size === nodes.length;
            if (canRestoreSettled) {
                this.simulation.stop().alpha(0);
                this.layoutSettled = true;
                this.graphSimulationDone = true;
                this.graphProgress = 95;
                this.paintGraphPositions();
                this.restoreViewport(restoreState?.view?.zoom);
                this.finalizeLayout({ skipFit: true });
                return;
            }

            this.applyLayout(this.layoutMode);
            const restoredAlpha = Number(restoreState?.layout?.alpha);
            if (currentLayout && Number.isFinite(restoredAlpha) && restoredAlpha > 0.02) {
                this.simulation.alpha(restoredAlpha).restart();
            }
            if (currentLayout && restoreState?.layout?.settled && restoreState?.view?.zoom) this.restoreViewport(restoreState.view.zoom);
        },
        schedulePositionPaint() {
            if (this.positionRaf) return;
            this.positionRaf = requestAnimationFrame(() => {
                this.positionRaf = null;
                this.paintGraphPositions();
            });
        },
        paintGraphPositions() {
            this.linkSelection
                ?.attr('x1', d => (d.source.x ?? 0))
                .attr('y1', d => (d.source.y ?? 0))
                .attr('x2', d => (d.target.x ?? 0))
                .attr('y2', d => (d.target.y ?? 0));
            this.nodeSelection
                ?.attr('cx', d => (d.x ?? 0))
                .attr('cy', d => (d.y ?? 0));
            this.updateHighlightOverlayPositions();
        },
        restoreViewport(zoomState) {
            if (!zoomState || !this.zoomSelection || !this.zoomBehavior) return;
            const restored = restoreTransformFromWorldCenter(
                zoomState.worldCenter,
                zoomState.scale,
                this.simWidth,
                this.simHeight,
            );
            this.zoomSelection.call(
                this.zoomBehavior.transform,
                d3.zoomIdentity.translate(restored.x, restored.y).scale(restored.k),
            );
            this.viewportFitApplied = true;
        },
        finalizeLayout({ skipFit = false } = {}) {
            if (this.positionRaf) cancelAnimationFrame(this.positionRaf);
            this.positionRaf = null;
            if (!this.layoutSettled) this.arrangeClusterGroups();
            this.layoutSettled = true;
            this.paintGraphPositions();
            this.graphSimulationDone = true;
            if (this.graphProgress < 95) this.graphProgress = 95;

            this.refreshIslandLabels();
            this.refreshSurnameGroupBoundaries();
            this.refreshLayoutAffinityConnectors();
            this.highlightPersonInGraph();

            if (skipFit) {
                this.graphFitDone = true;
                this.refreshLabels();
                this._applyViewportCulling();
            } else if (!this.viewportFitApplied) {
                const fitStarted = this.fitGraphToViewport();
                if (!fitStarted) {
                    this.graphFitDone = true;
                    this.refreshLabels();
                    this._applyViewportCulling();
                }
            } else {
                this.graphFitDone = true;
                this.refreshLabels();
                this._applyViewportCulling();
            }
            this.markGraphLoadingComplete();
        },
        highlightPersonInGraph() {
            const svg = d3.select('#person-tree-container').select('svg');
            if (svg.empty()) return;
            const activePerson = this.activePersonId;

            if (!activePerson) {
                this.labelPriorityContext = {
                    selectedId: '',
                    targetId: null,
                    secondaryIds: new Set(),
                    pathIds: new Set(),
                };
                svg.selectAll('.nodes circle')
                    .classed('highlighted', false)
                    .classed('secondary', false)
                    .classed('dimmed', false)
                    .classed('path-to-thuemann', false)
                    .classed('thuemann-node', false);
                svg.selectAll('.links line')
                    .classed('highlighted', false)
                    .classed('secondary', false)
                    .classed('dimmed', false)
                    .classed('path-to-thuemann', false);
                svg.selectAll('.labels .label-item')
                    .classed('label-dimmed', false);
                svg.selectAll('.labels text').attr('fill', 'black');
                svg.selectAll('.labels image')
                    .attr('opacity', 1)
                    .attr('filter', null);
                svg.select('.component-labels').style('display', this.hasNameFilters ? 'none' : null);
                svg.select('.surname-group-boundaries').style('display', this.hasNameFilters ? 'none' : null);
                svg.select('.layout-affinity-connectors').style('display', this.hasNameFilters ? 'none' : null);
                this.clearHighlightOverlay();
                this.refreshLabels();
                this.applyNameFilterVisibility();
                return;
            }

            const links = svg.selectAll('.links line').data();
            const secondaryIds = new Set();

            const pathResults = this.activePathResults;
            const targetReached = pathResults[0]?.targetReached || null;
            const pathSet = new Set(pathResults.flatMap(result => result.path.map(person => person.id)));

            this.labelPriorityContext = {
                selectedId: activePerson,
                targetId: targetReached,
                secondaryIds: secondaryIds,
                pathIds: pathSet,
            };

            const pathEdges = new Set();
            pathResults.forEach((result) => {
                const ids = result.path.map(person => person.id);
                for (let i = 0; i < ids.length - 1; i++) {
                    pathEdges.add(`${ids[i]}-${ids[i + 1]}`);
                    pathEdges.add(`${ids[i + 1]}-${ids[i]}`);
                }
            });

            svg.selectAll('.nodes circle')
                .classed('highlighted', d => {
                    if (d.id === activePerson) {
                        links.forEach(l => {
                            if (l.source.id === d.id) secondaryIds.add(l.target.id);
                            if (l.target.id === d.id) secondaryIds.add(l.source.id);
                        });
                        return true;
                    }
                    return false;
                })
                .classed('secondary', d => secondaryIds.has(d.id) && !pathSet.has(d.id))
                .classed('path-to-thuemann', d => pathSet.has(d.id) && d.id !== activePerson && d.id !== targetReached)
                .classed('thuemann-node', d => d.id === targetReached)
                .classed('dimmed', d => !secondaryIds.has(d.id) && d.id !== activePerson && !pathSet.has(d.id))
                .filter(d => d.id === activePerson || secondaryIds.has(d.id) || pathSet.has(d.id))
                .each(function () { d3.select(this).raise(); });

            svg.selectAll('.links line')
                .classed('highlighted', d => d.source.id === activePerson || d.target.id === activePerson)
                .classed('path-to-thuemann', d => {
                    const k1 = `${d.source.id}-${d.target.id}`;
                    const k2 = `${d.target.id}-${d.source.id}`;
                    return pathEdges.has(k1) || pathEdges.has(k2);
                })
                .classed('secondary', d => {
                    const k1 = `${d.source.id}-${d.target.id}`;
                    const k2 = `${d.target.id}-${d.source.id}`;
                    return !pathEdges.has(k1) && !pathEdges.has(k2) &&
                        (secondaryIds.has(d.source.id) || secondaryIds.has(d.target.id));
                })
                .classed('dimmed', d => {
                    const k1 = `${d.source.id}-${d.target.id}`;
                    const k2 = `${d.target.id}-${d.source.id}`;
                    return !pathEdges.has(k1) && !pathEdges.has(k2) &&
                        d.source.id !== activePerson &&
                        d.target.id !== activePerson;
                });

            this.refreshHighlightOverlay(pathResults, pathSet, activePerson, targetReached);

            svg.select('.surname-group-boundaries').style('display', 'none');
            svg.select('.layout-affinity-connectors').style('display', 'none');

            svg.selectAll('.links line')
                .filter(d => {
                    const k1 = `${d.source.id}-${d.target.id}`;
                    const k2 = `${d.target.id}-${d.source.id}`;
                    return d.source.id === activePerson || d.target.id === activePerson
                        || pathEdges.has(k1) || pathEdges.has(k2);
                })
                .each(function () { d3.select(this).raise(); });

            this.refreshLabels();
            if (this.labelSelection) {
                this.labelSelection
                    .filter(d => d.id === activePerson || secondaryIds.has(d.id) || pathSet.has(d.id))
                    .each(function () { d3.select(this).raise(); });
            }
            this.applyNameFilterVisibility();
        },
        previewPersonPath() {
            return;
        },
        clearPreviewPerson(personId = '') {
            if (!this.previewPerson) return;
            if (personId && this.previewPerson !== personId) return;
            this.previewPerson = '';
            this.highlightPersonInGraph();
        },
        teardownGraph() {
            clearTimeout(this.resizeTimer);
            if (this.simulation) {
                this.simulation.stop();
                this.simulation.on('tick', null).on('end', null);
                this.simulation = null;
            }
            if (this.positionRaf) cancelAnimationFrame(this.positionRaf);
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            if (this._zoomRaf) cancelAnimationFrame(this._zoomRaf);
            this.positionRaf = null;
            this.resizeRaf = null;
            this._zoomRaf = null;
            this.zoomSelection?.interrupt()?.on('.zoom', null).on('click', null);
            this.graphLoading = false;
            this.graphProgress = 0;
            this.graphSimulationDone = false;
            this.graphFitDone = false;
            this.graphRoot = null;
            this.nodeSelection = null;
            this.linkSelection = null;
            this.labelSelection = null;
            this.highlightOverlaySelection = null;
            this.islandLabelSelection = null;
            this.islandLabelCenters = null;
            this.islandLabelFallbackCentroids = null;
            this.surnameGroupBoundarySelection = null;
            this.affinityConnectorSelection = null;
            this.labelMeasureCache = {};
            this.labelPlacementTick = 0;
            this.viewportFitApplied = false;
            this.manualZoomDetected = false;
            this.currentZoomK = 1;
            this.layoutSettled = false;
            this.layoutCentroids = null;
            this.zoomSelection = null;
            this.zoomBehavior = null;
            this.labelPriorityContext = {
                selectedId: '',
                targetId: null,
                secondaryIds: new Set(),
                pathIds: new Set(),
            };
            d3.select('#person-tree-container').select('svg').remove();
        },
        resizeTree() {
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
                this.resizeRaf = null;
                if (!this.zoomSelection?.node() || !this.zoomBehavior) return;
                const container = d3.select('#person-tree-container').node();
                if (!container) return;
                const previous = d3.zoomTransform(this.zoomSelection.node());
                const worldCenter = getViewportWorldCenter(previous, this.simWidth, this.simHeight);
                this.simWidth = container.offsetWidth || this.simWidth;
                this.simHeight = container.offsetHeight || this.simHeight;
                this.zoomSelection.attr('width', this.simWidth).attr('height', this.simHeight);
                const restored = restoreTransformFromWorldCenter(
                    worldCenter,
                    previous.k,
                    this.simWidth,
                    this.simHeight,
                );
                this.zoomSelection.call(
                    this.zoomBehavior.transform,
                    d3.zoomIdentity.translate(restored.x, restored.y).scale(restored.k),
                );
                if (this.layoutSettled) this.refreshLabels();
                if (this.layoutSettled && !this.manualZoomDetected) this.fitGraphToViewport(true);
            });
        },
        zoomIn() {
            const svg = d3.select('#person-tree-container').select('svg');
            if (!svg.empty() && this.zoomBehavior) {
                svg.transition().duration(500).call(this.zoomBehavior.scaleBy, 1.5);
            }
        },
        zoomOut() {
            const svg = d3.select('#person-tree-container').select('svg');
            if (!svg.empty() && this.zoomBehavior) {
                svg.transition().duration(500).call(this.zoomBehavior.scaleBy, 0.5);
            }
        },
        zoomToCluster(componentId) {
            this.zoomToComponents([componentId]);
        },
        zoomToComponents(componentIds) {
            const allowedComponentIds = new Set(componentIds || []);
            if (!allowedComponentIds.size) return;

            const positions = (this.simNodes || []).filter((node) => {
                if (!allowedComponentIds.has(node.componentId)) return false;
                return Number.isFinite(node.x) && Number.isFinite(node.y);
            });
            this.zoomToPositions(positions);
        },
        zoomToNodeIds(personIds) {
            const allowedPersonIds = new Set(personIds || []);
            if (!allowedPersonIds.size) return;
            const positions = (this.simNodes || []).filter(node => (
                allowedPersonIds.has(node.id) && Number.isFinite(node.x) && Number.isFinite(node.y)
            ));
            this.zoomToPositions(positions);
        },
        zoomToPositions(positions) {
            if (!this.zoomBehavior) return;
            const svg = d3.select('#person-tree-container').select('svg');
            if (svg.empty()) return;
            if (!positions.length) return;

            const xs = positions.map(n => n.x);
            const ys = positions.map(n => n.y);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);

            const container = d3.select('#person-tree-container').node();
            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight;
            const padding = 90;

            const clusterWidth = Math.max(maxX - minX, 1);
            const clusterHeight = Math.max(maxY - minY, 1);
            const scaleX = (containerWidth - padding * 2) / clusterWidth;
            const scaleY = (containerHeight - padding * 2) / clusterHeight;
            const scale = Math.max(Math.min(Math.min(scaleX, scaleY), 3), 0.01);

            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const translateX = containerWidth / 2 - midX * scale;
            const translateY = containerHeight / 2 - midY * scale;

            this.manualZoomDetected = true;
            svg.transition()
                .duration(700)
                .call(this.zoomBehavior.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
        },
        centerOnNode(personId) {
            if (!this.zoomBehavior) return;
            const svg = d3.select('#person-tree-container').select('svg');

            // Collect IDs of all nodes involved: selected + path to root + direct neighbours
            const relevantIds = new Set([personId]);
            (this.pathNodes || []).forEach(p => relevantIds.add(p.id));
            (this.secondaryNodes || []).forEach(p => relevantIds.add(p.id));

            // Look up current simulation positions
            const positions = (this.simNodes || []).filter(n => relevantIds.has(n.id));
            if (!positions.length) return;

            const xs = positions.map(n => n.x);
            const ys = positions.map(n => n.y);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);

            const container = d3.select('#person-tree-container').node();
            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight;
            const padding = 80;

            const pathWidth = maxX - minX || 1;
            const pathHeight = maxY - minY || 1;

            // Scale to fit entire path with padding; cap zoom-in at 3× for single/few nodes
            const scaleX = (containerWidth - padding * 2) / pathWidth;
            const scaleY = (containerHeight - padding * 2) / pathHeight;
            const scale = Math.min(scaleX, scaleY, 3);

            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const translateX = containerWidth / 2 - midX * scale;
            const translateY = containerHeight / 2 - midY * scale;

            svg.transition()
                .duration(750)
                .call(this.zoomBehavior.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
        },
        toggleSidebar() {
            this.sidebarCollapsed = !this.sidebarCollapsed;
        },
        toggleFullscreen() {
            const elem = document.documentElement;
            if (!this.isFullscreen) {
                if (elem.requestFullscreen) {
                    elem.requestFullscreen();
                } else if (elem.webkitRequestFullscreen) {
                    elem.webkitRequestFullscreen();
                } else if (elem.msRequestFullscreen) {
                    elem.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        },
        handleFullscreenChange() {
            this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
        },
        closeDropdown() {
            // Intentionally left as a parity hook with the achternamen shell.
        },
        goToSearch(node) {
            if (!node || this.isPrivateNode(node)) return;
            const query = node.label || '';
            this.$router.push(`/search?q=${encodeURIComponent(query)}`);
        },
        goToPerson(person) {
            if (!person?.id || this.isPrivateNode(person)) return;
            this.$router.push(`/person/${person.id}`);
        },
    },
};
</script>

<style>
.person-tree-container {
    --tree-main: rgba(174, 30, 37, 0.9);
    --tree-claasz: var(--brand-color-3);
    --tree-cluster: rgba(0, 0, 0, 0.68);
    --tree-molukse: var(--brand-bg-3);
    --tree-dna: #ffffff;
    position: relative;
    height: 100vh !important;
    width: 100% !important;
    overflow: visible;
}

#person-tree-container {
    position: absolute;
    inset: 0;
}

#person-tree-container.is-loading svg {
    opacity: 0;
    pointer-events: none;
}

/* D3 graph highlight states */
.person-tree-container .nodes circle {
    fill: var(--tree-main);
    opacity: 1;
    cursor: pointer;
}

.person-tree-container .nodes circle[data-category="main"] { fill: var(--tree-main); }
.person-tree-container .nodes circle[data-category="claasz"] { fill: var(--tree-claasz); }
.person-tree-container .nodes circle[data-category="cluster"] { fill: var(--tree-cluster); }
.person-tree-container .nodes circle[data-category="molukse"] { fill: var(--tree-molukse); }
.person-tree-container .nodes circle[data-category="tata-dna-match"] { fill: var(--tree-dna); }

.person-tree-container .nodes circle.subclade-gateway {
    stroke: #6f1016;
    stroke-width: 2.25px;
}

.person-tree-container .nodes circle.tata-dna-match {
    stroke: #6f7478;
    stroke-width: 1.5px;
}

.person-tree-container .nodes circle.highlighted {
    stroke: #6f1016;
    stroke-width: 3px;
    opacity: 1;
    r: 8;
}

.person-tree-container .nodes circle.secondary {
    stroke: #e07b80;
    stroke-width: 2px;
}

.person-tree-container .nodes circle.path-to-thuemann {
    stroke: #0066CC;
    stroke-width: 2px;
}

.person-tree-container .nodes circle.thuemann-node {
    stroke: #003d7a;
    stroke-width: 3px;
    opacity: 1;
}

.person-tree-container .nodes circle.dimmed {
    opacity: 0.2;
}

.person-tree-container .links line {
    stroke: #bbb;
}

.person-tree-container .links line.main-subclade {
    stroke: rgba(174, 30, 37, 0.46);
}

.person-tree-container .links line.highlighted {
    stroke: #ae1e25;
    stroke-width: 2;
}

.person-tree-container .links line.path-to-thuemann {
    stroke: #0066CC;
    stroke-width: 2;
}

.person-tree-container .links line.secondary {
    stroke: #e07b80;
}

.person-tree-container .links line.dimmed {
    stroke: #e2e2e2;
}

.person-tree-container .highlight-overlay {
    pointer-events: none;
}

.person-tree-container .highlight-overlay__link {
    stroke: #0066CC;
    stroke-width: 3.25px;
    stroke-linecap: round;
    opacity: 0.98;
    pointer-events: none;
}

.person-tree-container .highlight-overlay__node {
    fill: none;
    stroke: #0066CC;
    stroke-width: 3px;
    cursor: pointer;
    pointer-events: all;
}

.person-tree-container .highlight-overlay__node--selected {
    fill: none;
}

.person-tree-container .highlight-overlay__node--target {
    fill: none;
}

.surname-group-boundaries {
    pointer-events: none;
}

.layout-affinity-connectors {
    pointer-events: none;
}

.layout-affinity-connector {
    fill: none;
    stroke: rgba(71, 71, 71, 0.44);
    stroke-width: 1.5;
    stroke-dasharray: 5 7;
    stroke-linecap: round;
}

.layout-affinity-connector--adoptive {
    stroke: rgba(79, 96, 119, 0.58);
    stroke-width: 1.8;
}

.layout-affinity-connector--research {
    stroke: rgba(121, 85, 72, 0.48);
}

.layout-affinity-connector--cultural {
    stroke: rgba(121, 85, 72, 0.56);
}

.surname-group-boundary {
    fill: rgba(0, 0, 0, 0);
    stroke: rgba(35, 35, 35, 0.52);
    stroke-width: 1.4;
    stroke-dasharray: 10 8;
    stroke-linejoin: round;
    opacity: 0.72;
}

.surname-group-boundary--molukse {
    stroke: rgba(121, 85, 72, 0.68);
}

.surname-group-boundary--claasz {
    stroke: rgba(214, 164, 32, 0.9);
    stroke-width: 1.7;
}

.surname-group-boundary--tata-dna-match {
    stroke: rgba(111, 116, 120, 0.58);
    stroke-width: 1.5;
}

.surname-group-boundary--main-subclade {
    fill: rgba(174, 30, 37, 0.045);
    stroke: rgba(174, 30, 37, 0.82);
    stroke-width: 2;
    stroke-dasharray: 12 7;
    opacity: 0.9;
}

.component-labels {
    pointer-events: none;
}

.component-label {
    pointer-events: all;
    cursor: pointer;
    will-change: transform;
}

.component-label--hovering .component-label__bg {
    stroke-width: 1.5;
}

.component-label__proximity {
    fill: rgba(0, 0, 0, 0);
    stroke: none;
    pointer-events: all;
}

.component-label__bg {
    fill: var(--tree-cluster);
    stroke: rgba(0, 0, 0, 0.85);
    stroke-width: 1;
}

.component-label--main .component-label__bg {
    fill: var(--tree-main);
    stroke: rgba(174, 30, 37, 0.95);
}

.component-label--main-subclade .component-label__bg {
    fill: var(--tree-main);
    stroke: rgba(111, 16, 22, 0.96);
}

.component-label--claasz .component-label__bg {
    fill: var(--tree-claasz);
    stroke: rgba(214, 158, 0, 0.95);
}

.component-label--molukse .component-label__bg {
    fill: var(--tree-molukse);
    stroke: var(--brand-bg-5);
}

.component-label--tata-dna-match .component-label__bg {
    fill: var(--tree-dna);
    stroke: #6f7478;
}

.component-label__text {
    fill: #ffffff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
}

.component-label--claasz .component-label__text {
    fill: #2f2500;
    font-weight: 700;
}

.component-label--tata-dna-match .component-label__text {
    fill: var(--brand-text-dark);
    font-weight: 700;
}

.component-label--main .component-label__text {
    font-weight: 700;
}

.tree-legend {
    position: absolute;
    bottom: 20px;
    left: 20px;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 14px;
    max-width: calc(100vw - 32px);
    flex-direction: column;
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(2px);
}

.tree-legend__item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: opacity 0.18s ease;
}

.tree-legend__item:hover .tree-legend__label {
    text-decoration: underline;
}

.tree-legend__item--disabled {
    opacity: 0.38;
}

.tree-legend__item--disabled .tree-legend__swatch {
    filter: grayscale(0.75);
}

.tree-legend__labels {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.tree-legend__swatch {
    width: 30px;
    height: 30px;
    border-radius: 4px;
    flex-shrink: 0;
}

.tree-legend__swatch--main {
    background: var(--tree-main);
}

.tree-legend__swatch--claasz {
    background: var(--tree-claasz);
}

.tree-legend__swatch--cluster {
    background: var(--tree-cluster);
}

.tree-legend__swatch--molukse {
    background: var(--tree-molukse);
}

.tree-legend__swatch--tata-dna-match {
    background: var(--tree-dna);
    border: 1px solid #6f7478;
    box-sizing: border-box;
}

.tree-legend__label {
    color: #222;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.3;
}

.tree-legend__sublabel {
    color: #222;
    font-size: 10.5px;
    font-weight: 400;
    opacity: 0.65;
    white-space: nowrap;
    line-height: 1.3;
}

.tree-legend-path {
    position: absolute;
    top: 74px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 110;
    display: flex;
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    max-width: calc(100vw - 56px);
    width: 100%;
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    max-height: min(38vh, 320px);
    overflow-y: auto;
}

.tree-legend-path__title {
    color: #222;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.tree-legend-path__value {
    min-width: 0;
    color: #24313d;
    font-size: 12px;
    line-height: 1.35;
    white-space: normal;
}

.tree-route-swatch {
    display: inline-block;
    width: 9px;
    height: 9px;
    margin-right: 6px;
    border-radius: 999px;
}

.tree-legend-path__details {
    align-self: flex-start;
    color: #1768ac;
    font-size: 12px;
    text-decoration: underline;
}

@media (max-width: 980px) {
    .tree-legend-path {
        top: 146px;
        left: 12px;
        right: 12px;
        transform: none;
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;
        max-width: none;
    }

    .tree-legend__label,
    .tree-legend__sublabel {
        white-space: normal;
    }
}

.person-tree-status--overlay {
    position: absolute;
    inset: 0;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(7, 9, 14, 0.88);
    color: #0d1117;
}

.person-tree-progress {
    width: min(440px, calc(100vw - 56px));
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.person-tree-progress__bar {
    width: 100%;
    height: 10px;
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    overflow: hidden;
}

.person-tree-progress__fill {
    height: 100%;
    width: 0;
    background: linear-gradient(90deg, rgba(174, 30, 37, 0.85) 0%, rgba(224, 123, 128, 0.95) 100%);
    transition: width 0.22s ease;
}

.person-tree-progress__value {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.person-tree-container:hover {
    cursor: grab;
}

.person-tree-container:active {
    cursor: grabbing;
}

#zoom-buttons {
    position: absolute;
    bottom: 24px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 100;
}

#zoom-buttons button {
    padding: 0;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0);
    border: none;
    border-radius: var(--border-radius-pill);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
}

#zoom-buttons button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button {
    position: absolute;
    top: 24px;
    right: 24px;
    height: 28px;
    width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: var(--border-radius-pill);
    box-shadow: 0 0 0 0 var(--brand-color-1);
    z-index: 100;
}

#fullscreen-button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button img {
    width: 16px;
    height: 16px;
}

#toggle-sidebar-button {
    position: absolute;
    top: 50%;
    left: 342px;
    transform: translateY(-50%);
    padding: 8px;
    cursor: pointer;
    background-color: #fff;
    border: none;
    border-radius: var(--border-radius-md);
    transition: var(--transition-ease);
    box-shadow: var(--shadow-card-sm);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
}

#toggle-sidebar-button.sidebar-collapsed {
    left: 9px;
}

#toggle-sidebar-button:hover {
    background-color: #f0f0f0;
}

#toggle-sidebar-button img {
    width: 20px;
    height: 20px;
}

#person-tree-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.links line {
    stroke: #aaa;
    stroke-width: 1px;
}

.nodes circle {
    cursor: pointer;
    stroke: #fff;
    stroke-width: 1.5px;
}

.nodes circle:hover {
    stroke: black;
}

.labels .label-item {
    cursor: pointer;
    pointer-events: all;
}

.labels text {
    font-family: Arial, sans-serif;
    pointer-events: none;
    paint-order: stroke;
    stroke: rgba(255, 255, 255, 0.95);
    stroke-width: 2.5px;
    stroke-linejoin: round;
    fill: #111;
}

.labels .label-lock {
    pointer-events: none;
}

.labels .label-dimmed .label-lock {
    opacity: 0.35;
}

.person-list-container h1 {
    font-family: var(--font-primary);
    color: var(--brand-color-1);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%;
    text-transform: lowercase;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.person-tree-status {
    position: absolute;
    top: 24px;
    padding: 12px 16px;
    background: #fff;
    z-index: 20000;
    width: 100%;
    left: 0;
    top: 0;
}

.person-tree-status--error {
    color: #9f1239;
}

.person-list-container {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    padding: 2px 0;
    background-color: #fff;
    width: 360px;
    max-width: min(360px, 86vw);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    box-shadow: var(--shadow-card-lg);
    transition: transform .3s ease, opacity .3s ease;
}

.person-list-container.collapsed {
    transform: translateX(-100%);
    pointer-events: none;
}

.person-item {
    box-sizing: border-box;
    min-height: 40px;
    position: relative;
    padding: 8px calc(24px + 28px + 8px) 8px 24px;
    cursor: pointer;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.person-name {
    display: block;
    padding-right: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.person-list-lock {
    display: inline-block;
    width: 10px;
    height: 12px;
    vertical-align: -1px;
}

.person-list-search {
    position: sticky;
    top: 0;
    z-index: 11;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
}

.person-list-search input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: var(--border-radius-md);
    padding: 8px 10px;
    font-size: 13px;
    outline: none;
}

.person-list-search input:focus {
    border-color: var(--brand-color-1);
    box-shadow: 0 0 0 2px rgba(174, 30, 37, 0.15);
}

.person-name-filter {
    padding: 12px;
    background: #fff8f8;
    border-bottom: 1px solid #ead7d8;
}

.person-name-filter label {
    display: block;
    margin-bottom: 6px;
    color: var(--brand-text-dark);
    font-size: 13px;
    font-weight: 700;
}

.person-name-filter__controls {
    display: flex;
    gap: 6px;
}

.person-name-filter__controls input {
    min-width: 0;
    flex: 1;
    box-sizing: border-box;
    border: 1px solid #d8c4c5;
    border-radius: var(--border-radius-md);
    padding: 8px 10px;
    font-size: 13px;
    outline: none;
}

.person-name-filter__controls input:focus {
    border-color: var(--brand-color-1);
    box-shadow: 0 0 0 2px rgba(174, 30, 37, 0.15);
}

.person-name-filter__controls button {
    border: 0;
    border-radius: var(--border-radius-md);
    padding: 0 10px;
    color: #fff;
    background: var(--brand-color-1);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.person-name-filter__controls button:disabled {
    opacity: 0.45;
    cursor: default;
}

.person-name-filter__hint,
.person-name-filter__result {
    margin: 6px 0 0;
    color: #68595a;
    font-size: 11px;
    line-height: 1.35;
}

.person-name-filter__result {
    font-weight: 600;
}

.person-name-filter__chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
}

.person-name-filter__chip,
.person-name-filter__clear {
    border: 1px solid rgba(174, 30, 37, 0.25);
    border-radius: 999px;
    padding: 4px 8px;
    color: var(--brand-color-1);
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.person-name-filter__chip--empty {
    border-style: dashed;
    opacity: 0.62;
}

.person-name-filter__clear {
    border-color: transparent;
    padding-inline: 4px;
    color: #68595a;
    background: transparent;
    font-weight: 500;
    text-decoration: underline;
}

.person-item:hover .dropdown-toggle {
    display: inline-block;
}

.person-list-container .dropdown {
    position: absolute;
    right: 24px;
    top: 6px;
    display: inline-block;
}

.person-list-container .dropdown-toggle {
    display: none;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: 4px;
    padding: 6px 9px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.person-list-container .dropdown-toggle:hover {
    background-color: var(--brand-color-2);
}

.person-list-container .dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: -12px;
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    border-radius: 16px;
    z-index: 1000;
    padding: 8px 0;
    overflow: hidden;
}

.person-list-container .dropdown:hover .dropdown-menu {
    display: block;
}

.person-list-container .dropdown-menu button {
    width: 100%;
    padding: 8px 24px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
}

.person-list-container .dropdown-menu button:hover {
    background-color: #f5f5f5;
    text-decoration: underline;
}

.person-item:last-child {
    border-bottom: none;
}

.person-item:hover {
    background-color: #f0f0f0;
    text-decoration: underline;
}

.person-item.highlighted {
    background-color: #f0f0f0;
    color: var(--brand-color-1);
    font-weight: bold;
    z-index: 10;
}

.person-item.highlighted:hover {
    text-decoration: none;
}

.secondary-label,
.secondary-item,
.path-label,
.path-item {
    color: var(--brand-text-dark);
    font-weight: 400;
    font-size: 12px;
}

.secondary-list,
.path-list {
    padding-left: 0;
    list-style: none;
}

.path-route {
    margin-top: 6px;
    padding-left: 10px;
    border-left: 3px solid var(--route-color, #1768ac);
    list-style: none;
}

.path-route .path-item {
    display: block;
}

.secondary-item,
.path-item {
    padding-left: 16px;
    position: relative;
}

.secondary-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--brand-color-3);
    border-radius: var(--border-radius-pill);
    border: 1px solid var(--brand-text-dark);
}

.path-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--route-color, #1768ac);
    border-radius: var(--border-radius-pill);
    border: 1px solid var(--route-color, #1768ac);
}

@media (max-width: 860px) {
    .person-tree-container .tree-legend {
        bottom: 96px;
    }

    .person-list-container {
        width: min(360px, 86vw);
    }

    #fullscreen-button {
        top: 16px;
        right: 16px;
    }

    .person-tree-container #zoom-buttons {
        bottom: 96px;
        right: 16px;
    }
}
</style>
